import { useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { prefetchSheets } from "../lib/pdfRender";
import { useProject, actions, money, outstanding, currentStage, stageProgress, STUDIO, useRateCard } from "../lib/store";
import { useReveal } from "../lib/anim";
import { useIsMobile } from "../lib/useMobile";
import { NAV_GROUPS } from "../lib/portalNav";
import { Icon, type IconName } from "../components/Icons";
import { Avatar, Plate, Meter, StatusBadge, Modal } from "../components/ui";
import { SignaturePad } from "../components/SignaturePad";
import { ScopeConfigurator } from "../components/ScopeConfigurator";
import { defaultConfig, computeEstimate } from "../lib/rates";
import { totalArea, unitLabel, isInterior, newSpace } from "../lib/program";
import { fileToDataUri } from "../lib/image";
import { storeImage, storeFile } from "../lib/sync";
import { AddReference } from "../studio/Brief";
import EngagementDoc from "../studio/EngagementDoc";
import QuotationDoc from "../studio/QuotationDoc";
import AgreedFeeDoc from "../components/AgreedFeeDoc";
import { StageLightbox, stageState, TONE_CLASS } from "../components/StageDetail";
import { ChangeRegister } from "../components/ChangeRegister";
import { ActionCenter } from "../components/ActionCenter";
import { BudgetStudio } from "../components/BudgetStudio";
import { RecordBadge, RecordsPanel } from "../components/RecordStatus";
import { ConceptReview } from "../components/ConceptReview";
import { FeeByPhase } from "../components/FeeByPhase";
import { FilmCard } from "../components/FilmFrame";
import { TenderRoom } from "../components/TenderRoom";
import { clientTodos } from "../lib/todos";
import { placementOf, splitNav } from "../lib/portalNav";
import { billingTotals, valuationTotals, monthlyDraw, confirmedPaid, claimedPaid, feeDiscount } from "../lib/billing";
import { UpcomingCard } from "../components/PaymentLedger";
import { DrawingViewer, sheetKeyOf, type ViewerSheet } from "../components/DrawingViewer";
import { ClientActions } from "../components/NextSteps";
import { SyncBadge } from "../components/SyncBadge";
import { useTheme } from "../components/Shell";
import { useSession, logout } from "../lib/auth";
import { openWhatsApp } from "../lib/whatsapp";
import { proposedMilestones, milestonesReady } from "../lib/milestones";
import { fmtDate, fmtDay, relative, daysUntil, todayISO } from "../lib/format";
import { downloadTransmittalPdf, downloadInvoicePdf, downloadSpaceReportPdf } from "../lib/pdf";
import { ClientBriefTracker } from "../components/BriefChecklist";
import { ClientTimeline } from "../components/ClientTimeline";
import { BookViewer } from "../components/BookViewer";
import { downloadSheetFiles } from "../lib/downloadSheets";
import type { DesignSpace, Consultant, ConsultantDiscipline } from "../lib/types";

type Tab = "overview" | "story" | "budget" | "concept" | "film" | "tender" | "engagement" | "fees" | "brief" | "moodboard" | "direction" | "configure" | "designs" | "drawings" | "packages" | "submissions" | "approvals" | "spaces" | "reviews" | "rfis" | "progress" | "consultants" | "reports" | "billing" | "messages";
/* Ordered the way the work actually happens: the project, then the brief (and
   the references that feed it), then the scope and what it costs, then the
   engagement to sign. Delivery follows once we're appointed. */
const TABS: { key: Tab; label: string }[] = [
  { key: "overview", label: "Overview" }, { key: "brief", label: "Brief" }, { key: "moodboard", label: "Mood board" },
  { key: "direction", label: "Direction" }, { key: "concept", label: "The concept" }, { key: "film", label: "Film" },
  { key: "budget", label: "Budget & size" }, { key: "fees", label: "Fees & scope" }, { key: "configure", label: "Configure" }, { key: "engagement", label: "Engagement" },
  { key: "story", label: "The story" },
  { key: "designs", label: "Designs" }, { key: "spaces", label: "Your spaces" }, { key: "drawings", label: "Drawings" }, { key: "packages", label: "Packages" }, { key: "submissions", label: "Stage submissions" }, { key: "tender", label: "Tendering" },
  { key: "approvals", label: "Approvals" }, { key: "reviews", label: "Reviews" }, { key: "rfis", label: "RFIs" }, { key: "progress", label: "Progress" },
  { key: "consultants", label: "Consultants" },
  { key: "reports", label: "Reports" }, { key: "billing", label: "Billing" }, { key: "messages", label: "Conversation" },
];

/* The setup rooms — relevant while the brief is taking shape, noise once the
   project is on the boards and being built. Curated out in delivery. */
const SETUP_TABS = new Set<Tab>(["brief", "moodboard", "direction", "configure", "engagement"]);
/** True once the project has reached documentation or beyond (it's executing,
   not being set up). Keeps the brief/mood-board/engagement out of the way. */
function portalExecuting(p: any): boolean {
  const stages = p.stages ?? [];
  const activeIdx = stages.findIndex((s: any) => s.status === "active");
  const curIdx = activeIdx >= 0 ? activeIdx : stages.length - 1;
  const docIdx = stages.findIndex((s: any) => s.key === "documentation");
  return docIdx >= 0 && curIdx >= docIdx;
}

export default function Portal() {
  const { pid, section } = useParams();
  const p = useProject(pid);
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>((TABS.some((t) => t.key === section) ? section : "overview") as Tab);
  const [theme, toggleTheme] = useTheme();
  const session = useSession();
  const isStudio = session?.role === "studio" || session?.role === "team";
  const ref = useReveal<HTMLDivElement>([tab]);
  const mobile = useIsMobile();
  // a tab change on a phone starts at the top of the new room, not mid-page
  useEffect(() => { if (mobile) window.scrollTo({ top: 0 }); }, [tab, mobile]);
  if (!p) return null;

  const pendingDesigns = p.presentation.spaces.filter((s) => s.response === "pending").length;
  const needsBrief = p.brief.status === "sent";
  const unseen = (p.notices ?? []).filter((n) => !n.seen);
  const accepted = p.engagement?.status === "signed";
  const owed = outstanding(p);
  // a feature the studio turned off never renders, even via a direct link
  const shownTab: Tab = (p.portalHidden ?? []).includes(tab) ? "overview" : tab;

  return (
    <div className="portal" style={{ minHeight: "100vh", background: "var(--paper)" }}>
      {/* top bar */}
      <div className="row between portal-top" style={{ padding: "14px clamp(18px,4vw,44px)", borderBottom: "1px solid var(--line)", position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--paper) 85%, transparent)", backdropFilter: "blur(12px)" }}>
        <div className="row" style={{ gap: 12, minWidth: 0 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "var(--ink)", color: "var(--paper)", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon.logo width={19} /></div>
          <div style={{ minWidth: 0 }}><div className="serif" style={{ fontSize: 16, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mobile ? p.name : STUDIO.name}</div><div className="kicker" style={{ fontSize: 9 }}>{mobile ? `${STUDIO.name} · Client Portal` : "Client Portal"}</div></div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <span className="m-hide"><SyncBadge compact /></span>
          <button className="iconbtn" onClick={toggleTheme} style={{ width: 34, height: 34 }}>{theme === "light" ? <Icon.moon width={16} /> : <Icon.sun width={16} />}</button>
          {isStudio
            ? <button className="btn ghost sm m-hide" onClick={() => nav("/studio/projects")}><Icon.swap width={14} />Cockpit</button>
            : <button className="btn ghost sm m-hide" onClick={() => { logout(); nav("/login"); }}><Icon.back width={14} />Sign out</button>}
          <Avatar name={p.client.name} hue={p.client.avatarHue} size={34} />
        </div>
      </div>

      {/* hero */}
      <div className={"portal-hero" + (mobile && shownTab !== "overview" ? " inner" : "")} style={{ maxWidth: 1080, margin: "0 auto", padding: "clamp(28px,5vw,56px) clamp(18px,4vw,44px) 0" }}>
        <div className="row between wrap" style={{ gap: 20 }}>
          <div>
            <div className="kicker">Welcome back</div>
            <h1 className="serif" style={{ fontSize: "clamp(34px,5vw,58px)", marginTop: 8, lineHeight: 1 }}>Hello, {p.client.name.split(" ")[0]}.</h1>
            <p className="muted" style={{ marginTop: 12, fontSize: 16, maxWidth: 440 }}>Here's where <b style={{ color: "var(--ink)" }}>{p.name}</b> stands today — {currentStage(p).label.toLowerCase()} is underway.</p>
          </div>
          <div className="card" style={{ padding: 20, minWidth: 240 }}>
            <div className="kicker">Overall progress</div>
            <div className="serif" style={{ fontSize: 40, color: "var(--accent-ink)", margin: "6px 0 12px" }}>{stageProgress(p)}%</div>
            <Meter value={stageProgress(p)} />
            <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>Now: {currentStage(p).label}</div>
          </div>
        </div>

        {/* action ribbon */}
        {(needsBrief || pendingDesigns > 0 || owed > 0) && (
          <div className="row wrap portal-ribbon" style={{ gap: 10, marginTop: 26 }}>
            {unseen.length > 0 && <button className="chip on" style={{ background: "var(--ok)", borderColor: "var(--ok)", color: "#fff" }} onClick={() => { setTab("progress"); actions.markNoticesSeen(p.id); }}>{unseen.length} update{unseen.length > 1 ? "s" : ""} — {unseen[0].title} →</button>}
            {needsBrief && <button className="chip on" onClick={() => setTab("brief")}>Brief awaiting your approval →</button>}
            {pendingDesigns > 0 && <button className="chip on" onClick={() => setTab("designs")} style={{ background: "var(--accent)", borderColor: "var(--accent)" }}>{pendingDesigns} design{pendingDesigns > 1 ? "s" : ""} to review →</button>}
            {owed > 0 && accepted && <button className="chip" onClick={() => setTab("billing")}>{money(owed, p.currency)} due →</button>}
            {!accepted && p.engagement?.status === "issued" && <button className="chip on" onClick={() => setTab("engagement")}>Your engagement is ready to sign →</button>}
          </div>
        )}

        {/* Navigation. The rooms that exist on this project, split into a short
            front row (no sideways scrolling) and a grouped menu for the rest. */}
        {!mobile && <PortalNav p={p} tab={tab} setTab={setTab} />}
      </div>

      {/* content */}
      <div ref={ref} className="portal-content" style={{ maxWidth: 1080, margin: "0 auto", padding: "30px clamp(18px,4vw,44px) 80px" }}>
        {mobile && shownTab !== "overview" && (
          <button onClick={() => setTab("overview")} className="row" style={{ gap: 6, background: "none", border: "none", padding: 0, marginBottom: 12, color: "var(--ink-3)", fontSize: 12.5, cursor: "pointer" }}>
            <Icon.back width={12} />Overview
          </button>
        )}
        {shownTab === "overview" && <Overview p={p} go={setTab} />}
        {shownTab === "engagement" && <div className="reveal"><h2 className="serif" style={{ fontSize: 30, marginBottom: 16 }}>Your engagement</h2><EngagementDoc p={p} mode="client" /><div style={{ marginTop: 22 }}><RecordsPanel p={p} kinds={["engagement-issued", "engagement-signed"]} title="Your copies" intro="When the engagement is issued and when it is signed, the record is emailed to you and to the studio. If a copy didn't reach you, send it again here." /></div></div>}
        {(shownTab === "fees" || (shownTab === "configure" && p.billing)) && (
          <div className="reveal">
            <h2 className="serif" style={{ fontSize: 30, marginBottom: 6 }}>Fees &amp; scope</h2>
            <p className="muted" style={{ marginBottom: 18, maxWidth: 620 }}>What each discipline covers, how the fee is spread across the design stages, and what you receive at each one — open any stage for the full detail.</p>
            {p.billing ? <AgreedFeeDoc p={p} mode="client" /> : <QuotationDoc p={p} mode="client" />}
            <FeeByPhase p={p} />
          </div>
        )}
        {shownTab === "brief" && <BriefTab p={p} />}
        {shownTab === "moodboard" && <MoodboardTab p={p} />}
        {shownTab === "direction" && <DirectionTab p={p} />}
        {shownTab === "configure" && !p.billing && <ConfigureTab p={p} go={setTab} />}
        {shownTab === "designs" && <DesignsTab p={p} />}
        {shownTab === "drawings" && <ClientDrawingsTab p={p} />}
        {shownTab === "packages" && <ClientPackagesTab p={p} who={session?.name ?? p.client.name} />}
        {shownTab === "submissions" && <ClientSubmissionsTab p={p} />}
        {shownTab === "reviews" && <ReviewsTab p={p} />}
        {shownTab === "rfis" && <ClientRfisTab p={p} />}
        {shownTab === "approvals" && <ClientApprovalsTab p={p} />}
        {shownTab === "spaces" && <ClientSpacesTab p={p} />}
        {shownTab === "story" && <ClientStoryTab p={p} />}
        {shownTab === "budget" && <ClientBudgetTab p={p} />}
        {shownTab === "concept" && <ConceptReview p={p} who={session?.name ?? p.client.name} />}
        {shownTab === "film" && <FilmTab p={p} />}
        {shownTab === "tender" && <TenderRoom p={p} who={session?.name ?? p.client.name} role="client" />}
        {shownTab === "progress" && <ProgressTab p={p} />}
        {shownTab === "consultants" && <ConsultantsTab p={p} />}
        {shownTab === "reports" && <ReportsTab p={p} />}
        {shownTab === "billing" && <BillingTab p={p} />}
        {shownTab === "messages" && <MessagesTab p={p} />}
      </div>
      {mobile && <MobilePortalBar p={p} tab={tab} setTab={setTab} isStudio={isStudio} onCockpit={() => nav("/studio/projects")} onSignOut={() => { logout(); nav("/login"); }} />}
    </div>
  );
}

/* ---------------------------------------------------------- phone chrome */
/* Five thumb-reach tabs along the bottom — home, the design, the money, the
   conversation, and everything else in a grouped sheet. The rooms shown are
   the same ones the desktop strip shows; nothing is reachable here that the
   studio turned off. */
const BAR_ICON: Record<string, IconName> = { overview: "dashboard", designs: "image", concept: "concept", drawings: "layers", billing: "money", fees: "money", messages: "chat" };
function MobilePortalBar({ p, tab, setTab, isStudio, onCockpit, onSignOut }: { p: any; tab: Tab; setTab: (t: Tab) => void; isStudio: boolean; onCockpit: () => void; onSignOut: () => void }) {
  const [open, setOpen] = useState(false);
  const available = availableTabs(p);
  const has = (k: Tab) => available.some((t) => t.key === k);
  const pick = (keys: Tab[]) => keys.find(has);
  const design = pick(["designs", "concept", "drawings", "moodboard", "direction"]);
  const owed = outstanding(p);
  const money = pick(owed > 0 ? ["billing", "fees"] : ["fees", "billing", "budget"]);
  const slots: { key: Tab; label: string; icon: IconName; badge?: number }[] = [
    { key: "overview", label: "Home", icon: "dashboard" },
    ...(design ? [{ key: design, label: design === "drawings" ? "Drawings" : "Design", icon: BAR_ICON[design] ?? "image", badge: design === "designs" ? (p.presentation?.spaces ?? []).filter((s: any) => s.response === "pending").length : 0 }] : []),
    ...(money ? [{ key: money, label: money === "billing" ? "Billing" : money === "budget" ? "Budget" : "Fees", icon: "money" as IconName, badge: money === "billing" ? (p.invoices ?? []).filter((i: any) => i.status === "due" || i.status === "overdue").length : 0 }] : []),
    ...(has("messages") ? [{ key: "messages" as Tab, label: "Messages", icon: "chat" as IconName, badge: (p.notices ?? []).filter((n: any) => !n.seen).length }] : []),
  ];
  const label = (t: { key: Tab; label: string }) => (t.key === "configure" && p.feeLock ? "Agreed scope" : t.label);
  const inSlots = new Set(slots.map((s) => s.key));
  const groups = NAV_GROUPS
    .map((g) => ({ label: g.label, tabs: g.tabs.map((k) => available.find((t) => t.key === k)).filter((t): t is { key: Tab; label: string } => !!t) }))
    .filter((g) => g.tabs.length > 0);
  const grouped = new Set(groups.flatMap((g) => g.tabs.map((t) => t.key)));
  const rest = available.filter((t) => !grouped.has(t.key));
  if (rest.length) groups.push({ label: "Everything else", tabs: rest });
  const moreActive = !inSlots.has(tab);
  return (
    <>
      <nav className="portal-bar">
        {slots.map((s) => {
          const on = tab === s.key;
          return (
            <button key={s.key} onClick={() => { setTab(s.key); setOpen(false); }} className="portal-tab" style={{ color: on ? "var(--accent-ink)" : "var(--ink-3)" }}>
              <span style={{ position: "relative" }}>{Icon[s.icon]({ width: 21 })}{!!s.badge && <span className="portal-dot">{s.badge}</span>}</span>
              <span style={{ fontSize: 10, fontWeight: 500 }}>{s.label}</span>
            </button>
          );
        })}
        <button onClick={() => setOpen(true)} className="portal-tab" style={{ color: moreActive || open ? "var(--accent-ink)" : "var(--ink-3)" }}>
          <Icon.grip width={21} /><span style={{ fontSize: 10, fontWeight: 500 }}>{moreActive ? label(available.find((t) => t.key === tab) ?? { key: tab, label: "More" }).slice(0, 10) : "More"}</span>
        </button>
      </nav>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 95, background: "rgba(20,14,8,.42)", backdropFilter: "blur(4px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", borderRadius: "22px 22px 0 0", border: "1px solid var(--line)", borderBottom: "none", padding: "10px 16px calc(16px + env(safe-area-inset-bottom))", maxHeight: "84vh", overflow: "auto" }}>
            <div style={{ width: 38, height: 4.5, borderRadius: 4, background: "var(--line-2)", margin: "2px auto 12px" }} />
            {groups.map((g) => (
              <div key={g.label} style={{ marginBottom: 12 }}>
                <div className="kicker" style={{ fontSize: 9, marginBottom: 6 }}>{g.label}</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
                  {g.tabs.map((t) => {
                    const on = tab === t.key;
                    return (
                      <button key={t.key} onClick={() => { setTab(t.key); setOpen(false); }}
                        style={{ textAlign: "left", padding: "11px 12px", borderRadius: 12, border: "1px solid " + (on ? "var(--accent)" : "var(--line)"), background: on ? "var(--accent-soft)" : "var(--card)", cursor: "pointer", fontSize: 13.5, color: on ? "var(--accent-ink)" : "var(--ink)" }}>
                        {label(t)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="row between wrap" style={{ gap: 8, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
              <SyncBadge compact />
              {isStudio
                ? <button className="btn ghost sm" onClick={onCockpit}><Icon.swap width={13} />Cockpit</button>
                : <button className="btn ghost sm" onClick={onSignOut}><Icon.back width={13} />Sign out</button>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* The tools a client reaches for from a phone — the one tap that moves the
   project, plus the places they come back to. Only what applies today. */
function QuickTools({ p, go }: { p: any; go: (t: Tab) => void }) {
  const available = new Set(availableTabs(p).map((t) => t.key));
  const owed = outstanding(p);
  const pendingDesigns = (p.presentation?.spaces ?? []).filter((s: any) => s.response === "pending").length;
  const tools: { key: Tab; label: string; icon: IconName; hot?: boolean }[] = [];
  if (p.brief?.status === "sent" && available.has("brief")) tools.push({ key: "brief", label: "Approve the brief", icon: "brief", hot: true });
  if (p.engagement?.status === "issued" && available.has("engagement")) tools.push({ key: "engagement", label: "Sign the engagement", icon: "sign", hot: true });
  if (pendingDesigns > 0 && available.has("designs")) tools.push({ key: "designs", label: `Review ${pendingDesigns} design${pendingDesigns === 1 ? "" : "s"}`, icon: "image", hot: true });
  if (p.deck?.status === "issued" && !(p.deck.options ?? []).some((o: any) => o.response?.choice === "preferred") && available.has("concept")) tools.push({ key: "concept", label: "Choose a concept", icon: "concept", hot: true });
  if ((p.approvals ?? []).some((a: any) => a.status === "issued" || a.status === "pending") && available.has("approvals")) tools.push({ key: "approvals", label: "Approvals waiting", icon: "check", hot: true });
  if (owed > 0 && available.has("billing")) tools.push({ key: "billing", label: `Pay ${money(owed, p.currency)}`, icon: "money", hot: true });
  if (available.has("messages")) tools.push({ key: "messages", label: "Message us", icon: "chat" });
  if (available.has("drawings")) tools.push({ key: "drawings", label: "Drawings", icon: "layers" });
  if (available.has("moodboard")) tools.push({ key: "moodboard", label: "Add references", icon: "palette" });
  if (available.has("progress")) tools.push({ key: "progress", label: "Progress", icon: "stages" });
  if (available.has("billing") && !(owed > 0)) tools.push({ key: "billing", label: "Invoices & receipts", icon: "money" });
  if (!tools.length) return null;
  return (
    <div className="m-only portal-tools" style={{ gridColumn: "1 / -1" }}>
      {tools.map((t) => (
        <button key={t.key + t.label} onClick={() => go(t.key)} className="chip" style={t.hot ? { background: "var(--accent)", borderColor: "var(--accent)", color: "#fff", padding: "10px 14px", fontSize: 13 } : { padding: "10px 14px", fontSize: 13 }}>
          {Icon[t.icon]({ width: 13, style: { marginRight: 6, verticalAlign: "-2px" } })}{t.label}
        </button>
      ))}
    </div>
  );
}


/* ------------------------------------------------------- portal navigation */
/* Which rooms exist at all on this project. A room the studio has nothing in
   yet simply isn't there — the client shouldn't find empty pages. */
function availableTabs(p: any): { key: Tab; label: string }[] {
  return TABS
    .filter((t) => t.key !== "direction" || (p.conceptsPresentedOn && (p.concepts ?? []).length > 0))
    // the configurator prices off the rate card; on a project billed from a signed
    // contract that quotes a fee nobody agreed to — the contract is the fee
    .filter((t) => t.key !== "configure" || !p.billing)
    // old presentations stay reachable after the live deck closes — the archive
    // is part of the record the client is entitled to
    .filter((t) => t.key !== "concept" || p.deck?.status === "issued" || (p.deckArchive ?? []).some((d: any) => d.status === "issued"))
    .filter((t) => t.key !== "film" || (p.films ?? []).some((f: any) => f.sharedWithClient))
    .filter((t) => t.key !== "drawings" || (p.deliverables ?? []).some((d: any) => d.previewUrl || d.fileUrl) || (p.drawingSets ?? []).some((s: any) => s.sharedWithClient))
    .filter((t) => t.key !== "packages" || (p.packages ?? []).some((s: any) => s.audience === "client"))
    .filter((t) => t.key !== "submissions" || (p.submissions ?? []).length > 0)
    .filter((t) => t.key !== "tender" || (p.tenders ?? []).length > 0 || (p.deliverables ?? []).some((d: any) => d.fileUrl || d.previewUrl))
    .filter((t) => t.key !== "rfis" || (p.rfis ?? []).length > 0 || portalExecuting(p))
    .filter((t) => t.key !== "approvals" || (p.approvals ?? []).length > 0)
    .filter((t) => t.key !== "spaces" || ((p.brief?.spaces ?? []).length > 0))
    .filter((t) => placementOf(p, t.key) !== "off");
}

function PortalNav({ p, tab, setTab }: { p: any; tab: Tab; setTab: (t: Tab) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { front, groups } = splitNav(p, availableTabs(p));
  const label = (t: { key: Tab; label: string }) => (t.key === "configure" && p.feeLock ? "Agreed scope" : t.label);
  const inMenu = groups.some((g) => g.tabs.some((t) => t.key === tab));

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  const tabBtn = (t: { key: Tab; label: string }) => (
    <button key={t.key} onClick={() => { setTab(t.key); setOpen(false); }}
      style={{ padding: "12px 15px", background: "none", border: "none", borderBottom: "2px solid " + (tab === t.key ? "var(--accent)" : "transparent"), color: tab === t.key ? "var(--ink)" : "var(--ink-3)", fontWeight: 500, fontSize: 14.5, cursor: "pointer", whiteSpace: "nowrap", transition: "color .2s" }}>
      {label(t)}
    </button>
  );

  return (
    <div ref={box} style={{ position: "relative", marginTop: 30, borderBottom: "1px solid var(--line)" }}>
      <div className="row wrap" style={{ gap: 2, alignItems: "flex-end" }}>
        {front.map(tabBtn)}
        {groups.length > 0 && (
          <button onClick={() => setOpen((v) => !v)}
            style={{ padding: "12px 14px", background: "none", border: "none", borderBottom: "2px solid " + (inMenu ? "var(--accent)" : "transparent"), color: inMenu || open ? "var(--ink)" : "var(--ink-3)", fontWeight: 500, fontSize: 14.5, cursor: "pointer", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {inMenu ? label(groups.flatMap((g) => g.tabs).find((t) => t.key === tab)!) : "Everything else"}
            <Icon.caretDown width={12} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .18s" }} />
          </button>
        )}
      </div>

      {open && (
        <div className="card" style={{ position: "absolute", top: "100%", right: 0, marginTop: 6, zIndex: 40, padding: "14px 4px", minWidth: 280, maxWidth: "min(620px, 92vw)", boxShadow: "0 18px 50px rgba(0,0,0,.14)", display: "grid", gridTemplateColumns: groups.length > 3 ? "1fr 1fr" : "1fr", gap: "4px 18px" }}>
          {groups.map((g) => (
            <div key={g.label} style={{ padding: "0 14px 10px" }}>
              <div className="kicker" style={{ fontSize: 8.5, marginBottom: 5 }}>{g.label}</div>
              <div className="col" style={{ gap: 1 }}>
                {g.tabs.map((t) => (
                  <button key={t.key} onClick={() => { setTab(t.key); setOpen(false); }}
                    style={{ textAlign: "left", background: tab === t.key ? "var(--paper-2)" : "none", border: "none", padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 13.5, color: tab === t.key ? "var(--ink)" : "var(--ink-2)" }}>
                    {label(t)}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Overview */
/* Before the appointment is accepted the portal leads with the work, in the
   order it actually happens: the project, then the brief, then the scope, then
   the engagement to sign. */
function GetStarted({ p, go }: { p: any; go: (t: Tab) => void }) {
  const brief = p.brief?.status;
  const spaces = (p.brief?.spaces ?? []).length;
  const eng = p.engagement?.status;
  const steps = [
    {
      key: "project", label: "Your project", tab: "overview" as Tab,
      done: true, note: `${p.type}${p.location ? " · " + p.location : ""}`, cta: null,
    },
    {
      key: "brief", label: "The brief", tab: "brief" as Tab,
      done: brief === "approved",
      note: brief === "approved" ? "Approved — thank you"
        : brief === "sent" ? "Ready for your approval"
        : "Tell us what you need",
      cta: brief === "approved" ? null : brief === "sent" ? "Review & approve" : "Fill it in",
    },
    {
      key: "scope", label: "Scope & spaces", tab: "brief" as Tab,
      done: spaces > 0,
      note: spaces > 0 ? `${spaces} spaces · ${totalArea(p.brief.spaces).toLocaleString()} ${unitLabel(p.brief.areaUnit)}` : "Not set yet",
      cta: spaces > 0 ? "Adjust" : "Add your spaces",
    },
    {
      key: "engagement", label: "Engagement", tab: "engagement" as Tab,
      done: eng === "signed",
      note: eng === "signed" ? "Signed — we're underway" : "Ready for your signature",
      cta: eng === "signed" ? null : "Review & sign",
    },
  ];
  const doneCount = steps.filter((s) => s.done).length;

  return (
    <div className="card pad reveal" style={{ gridColumn: "1 / -1" }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 14 }}>
        <div>
          <div className="kicker">Getting your project underway</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{doneCount} of {steps.length} done — we'll walk it through with you</div>
        </div>
        <span className="serif" style={{ fontSize: 24 }}>{Math.round((doneCount / steps.length) * 100)}%</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
        {steps.map((s, i) => (
          <button key={s.key} className="panel" onClick={() => go(s.tab)}
            style={{ padding: "12px 13px", textAlign: "left", cursor: "pointer", borderColor: s.done ? "color-mix(in srgb,var(--ok) 35%,var(--line))" : "var(--line)", background: s.done ? "color-mix(in srgb,var(--ok) 5%,var(--card))" : "var(--card)" }}>
            <div className="row" style={{ gap: 8 }}>
              <span style={{ width: 18, height: 18, borderRadius: 18, flexShrink: 0, display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700,
                background: s.done ? "var(--ok)" : "var(--paper-2)", color: s.done ? "#fff" : "var(--ink-3)", border: "1px solid " + (s.done ? "var(--ok)" : "var(--line-2)") }}>
                {s.done ? <Icon.check width={10} /> : i + 1}
              </span>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{s.label}</span>
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 5, lineHeight: 1.4 }}>{s.note}</div>
            {s.cta && <div className="row" style={{ gap: 4, fontSize: 11.5, color: "var(--accent-ink)", fontWeight: 600, marginTop: 6 }}>{s.cta}<Icon.arrow width={11} /></div>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Fact({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="kicker" style={{ fontSize: 9 }}>{label}</div>
      <div style={{ fontSize: 14.5, marginTop: 4, lineHeight: 1.35 }}>{value}</div>
      {sub && <div className="dim" style={{ fontSize: 11.5, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

/* The facts a client asks for on the phone: where it is, what it costs, what's
   outstanding, when the next thing happens, and what they were last sent. If
   they have to open a tab to find any of these, the portal has failed. */
function KeyFacts({ p, go }: { p: any; go: (t: Tab) => void }) {
  const rc = useRateCard();
  const est = p.estimate ? computeEstimate(p.estimate, rc) : null;
  const fee = p.billing?.contractSum ?? est?.total;
  const owed = outstanding(p);
  const stage = currentStage(p);
  const pct = stageProgress(p);
  const next = [...(p.schedule ?? [])].filter((e: any) => daysUntil(e.date) >= 0)
    .sort((a: any, b: any) => +new Date(a.date) - +new Date(b.date))[0];
  const sheets = (p.deliverables ?? []).filter((d: any) => d.previewUrl || d.fileUrl).length;
  const lastPack = (p.packages ?? []).filter((x: any) => x.audience === "client")
    .sort((a: any, b: any) => +new Date(b.sharedAt ?? 0) - +new Date(a.sharedAt ?? 0))[0];

  const facts: { label: string; value: string; sub?: string; tab?: Tab }[] = [];
  if (stage) facts.push({ label: "Stage", value: stage.label, sub: typeof pct === "number" ? `${Math.round(pct)}% through the programme` : undefined, tab: "progress" });
  if (fee) facts.push({ label: p.billing ? "Agreed fee" : "Design fee", value: money(fee, p.currency), sub: p.billing ? "as contracted" : "estimated", tab: "fees" });
  facts.push({ label: "Outstanding", value: owed > 0 ? money(owed, p.currency) : "Nothing due", sub: owed > 0 ? "on issued invoices" : "all invoices settled", tab: "billing" });
  if (next) facts.push({ label: "Next date", value: fmtDay(next.date), sub: next.title, tab: "progress" });
  if (sheets) facts.push({ label: "Drawings", value: `${sheets} sheet${sheets === 1 ? "" : "s"}`, sub: lastPack ? `last sent ${relative(lastPack.sharedAt)}` : "available to view", tab: "drawings" });
  if (p.deck?.status === "issued") {
    const chosen = (p.deck.options ?? []).find((o: any) => o.response?.choice === "preferred");
    facts.push({ label: "Concept", value: chosen ? chosen.name : "Awaiting your choice", sub: chosen ? "the direction you chose" : `${(p.deck.options ?? []).length} options to consider`, tab: "concept" });
  }
  if (facts.length < 2) return null;

  return (
    <div className="card pad reveal" style={{ gridColumn: "1 / -1" }}>
      <div className="kicker" style={{ marginBottom: 12 }}>Where {p.name} stands</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 2 }}>
        {facts.map((f, i) => (
          <button key={i} onClick={() => f.tab && go(f.tab)} disabled={!f.tab}
            style={{ textAlign: "left", background: "none", border: "none", borderLeft: i ? "1px solid var(--line)" : "none", padding: "4px 16px", cursor: f.tab ? "pointer" : "default" }}>
            <div className="dim" style={{ fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase" }}>{f.label}</div>
            <div className="serif" style={{ fontSize: 21, marginTop: 4, lineHeight: 1.15 }}>{f.value}</div>
            {f.sub && <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>{f.sub}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Overview({ p, go }: { p: any; go: (t: Tab) => void }) {
  const next = [...p.schedule].filter((e: any) => daysUntil(e.date) >= 0).sort((a: any, b: any) => +new Date(a.date) - +new Date(b.date))[0];
  const latest = p.reports.find((r: any) => r.status === "issued");
  const rc = useRateCard();
  const est = p.estimate ? computeEstimate(p.estimate, rc) : null;
  /* Until the appointment is accepted, the money isn't the headline — getting
     the project, brief and scope right is. The fee lives in Fees & scope and in
     the engagement until it's signed, then it earns the top of the page. */
  const accepted = p.engagement?.status === "signed";
  const todos = clientTodos(p);
  /* The rendered proposal as the hero — the newest render the studio has
     presented, preferring a render presentation over concept boards. Absent
     until there is one; a dashboard never fakes its hero. */
  const decks = [p.deck, ...(p.deckArchive ?? [])].filter((d: any) => d && d.status === "issued");
  const renderDeck = decks.find((d: any) => d.kind === "renders") ?? decks.find((d: any) => (d.options ?? []).some((o: any) => o.images?.[0]));
  const hero = renderDeck ? (renderDeck.options ?? []).find((o: any) => o.images?.[0])?.images?.[0] : undefined;
  return (
    <div className="portal-grid" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 18 }}>
      <QuickTools p={p} go={go} />
      {hero && (
        <button className="reveal" onClick={() => go("concept")} style={{ gridColumn: "1 / -1", position: "relative", padding: 0, border: "1px solid var(--line)", borderRadius: 16, overflow: "hidden", cursor: "pointer", textAlign: "left", background: "var(--card)" }}>
          <img src={hero} alt={renderDeck.title} style={{ width: "100%", height: "clamp(200px, 32vw, 340px)", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", inset: "auto 0 0 0", padding: "40px 22px 16px", background: "linear-gradient(transparent, rgba(12,10,8,.72))", color: "#f6f2ea" }}>
            <div className="row between wrap" style={{ gap: 10, alignItems: "flex-end" }}>
              <div>
                <div className="kicker" style={{ color: "rgba(246,242,234,.75)", fontSize: 9 }}>{renderDeck.kind === "renders" ? "The rendered proposal" : "The design proposal"}</div>
                <div className="serif" style={{ fontSize: "clamp(20px, 3vw, 28px)", marginTop: 4 }}>{renderDeck.title}</div>
              </div>
              <span className="row" style={{ gap: 6, fontSize: 12.5, fontWeight: 600 }}>View the presentation <Icon.arrowUpRight width={13} /></span>
            </div>
          </div>
        </button>
      )}
      {todos.length > 0 && <div style={{ gridColumn: "1 / -1" }}><ActionCenter items={todos} title="For your attention" onItem={(s) => go(s as Tab)} /></div>}
      <KeyFacts p={p} go={go} />
      {!accepted && <GetStarted p={p} go={go} />}
      <ClientActions p={p} />
      {accepted && (est || p.billing) && (() => {
        /* the agreed schedule is the truth once there is one; the estimate
           stands in until then. Either way the client sees the full fee and
           the percentage taken off it. */
        const bd = p.billing ? feeDiscount(p.billing) : null;
        const ccy = p.billing?.currency ?? p.currency;
        const total = p.billing ? p.billing.contractSum : est!.total;
        const full = p.billing ? bd?.full : est!.discount > 0 ? est!.gross : undefined;
        const pct = p.billing ? bd?.pct : est!.discount > 0 ? est!.discountPct : undefined;
        const saving = full ? full - total : 0;
        const has = !!full && saving > 0;
        return (
        <div className="card pad reveal" style={{ gridColumn: "1 / -1", background: has ? "color-mix(in srgb, var(--accent) 6%, var(--card))" : "var(--card)", borderColor: has ? "color-mix(in srgb,var(--accent) 30%,var(--line))" : "var(--line)" }}>
          <div className="row between wrap" style={{ gap: 16 }}>
            <div>
              <div className="kicker">Your design fee</div>
              <div className="row wrap" style={{ gap: 12, alignItems: "baseline", marginTop: 8 }}>
                <span className="serif" style={{ fontSize: 34 }}>{money(total, ccy)}</span>
                {has && <><span className="dim" style={{ textDecoration: "line-through", fontSize: 16 }}>{money(full!, ccy)}</span><span className="badge accent"><span className="dot" />{(pct ?? 0).toFixed((pct ?? 0) % 1 ? 1 : 0)}% discount applied</span></>}
              </div>
              {has && <div style={{ fontSize: 13, marginTop: 6 }}>Full fee {money(full!, ccy)} · you save <b>{money(saving, ccy)}</b></div>}
              <div className="dim" style={{ fontSize: 13, marginTop: 6 }}>
                {p.billing
                  ? (p.billing.feePct && p.billing.constructionBudget ? `${(p.billing.feePct * 100).toFixed(p.billing.feePct * 100 % 1 ? 1 : 0)}% of a ${money(p.billing.constructionBudget, ccy)} construction budget` : "As agreed in your engagement")
                  : `${(est!.pctOfBudget * 100).toFixed(1)}% of budget · ${money(est!.constructionAmount, p.currency)} paid during construction`}
              </div>
            </div>

            <button className="btn soft sm" onClick={() => go(p.billing ? "fees" : "configure")}><Icon.sliders width={15} />{p.billing ? "View fees & scope" : p.feeLock ? "View agreed scope" : "Configure scope"}</button>
          </div>
        </div>
        );
      })()}
      {/* project facts */}
      <div className="card pad reveal" style={{ gridColumn: "1 / -1" }}>
        <div className="row between wrap" style={{ gap: 10, marginBottom: 14 }}>
          <div className="kicker">Project facts</div>
          <span className="mono dim" style={{ fontSize: 12 }}>{p.code}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 18 }}>
          <Fact label="Building type" value={p.type} />
          <Fact label="Location" value={p.location || "—"} />
          <Fact label="Floor area" value={p.area || (p.gia ? `${p.gia.toLocaleString()} ${unitLabel(p.giaUnit)}` : "—")} />
          <Fact label="Construction budget" value={money(p.budget, p.currency)} />
          <Fact label="Survey plan" value={p.surveyPlan === "yes" ? "Available" : p.surveyPlan === "no" ? "Not available" : "To confirm"} />
          <Fact label="Appointed" value={fmtDate(p.createdOn)} />
          <Fact label="Accommodation" value={`${(p.brief.spaces ?? []).length} spaces`} sub={`${totalArea(p.brief.spaces).toLocaleString()} ${unitLabel(p.brief.areaUnit)}`} />
          <Fact label="Drawing register" value={`${(p.deliverables ?? []).length} sheets`} sub={`${(p.deliverables ?? []).filter((d: any) => d.status === "issued").length} issued to date`} />
          <Fact label="Current stage" value={currentStage(p).label} sub={`${stageProgress(p)}% complete`} />
          {next && <Fact label="Next milestone" value={next.title} sub={fmtDate(next.date)} />}
        </div>
      </div>

      <div className="card reveal" style={{ overflow: "hidden", gridColumn: "1 / -1" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
          <div style={{ padding: 26 }}>
            <div className="kicker">Your design, at a glance</div>
            <h2 className="serif" style={{ fontSize: 30, marginTop: 10 }}>{p.presentation.title}</h2>
            <p className="muted" style={{ marginTop: 10 }}>{p.presentation.spaces.length} spaces developed. {p.presentation.spaces.filter((s: any) => s.response === "approved").length} approved so far.</p>
            <button className="btn accent" style={{ marginTop: 18 }} onClick={() => go("designs")}><Icon.present width={16} />Review the designs</button>
          </div>
          <Plate hue={p.cover} h={220} seed={2} />
        </div>
      </div>
      <div className="card pad reveal">
        <div className="kicker" style={{ marginBottom: 10 }}>Next together</div>
        {next ? <><div className="serif" style={{ fontSize: 22 }}>{next.title}</div><div className="muted" style={{ marginTop: 6 }}>{fmtDate(next.date)} · {relative(next.date)}</div></> : <div className="dim">Nothing scheduled.</div>}
      </div>
      <div className="card pad reveal">
        <div className="kicker" style={{ marginBottom: 10 }}>Latest report</div>
        {latest ? <><div className="serif" style={{ fontSize: 20 }}>{latest.title}</div><p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>{latest.summary.slice(0, 120)}…</p><button className="btn soft sm" style={{ marginTop: 12 }} onClick={() => go("reports")}>Read</button></> : <div className="dim">No reports yet.</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- Brief */
/* When there's no survey on file, ask the client to supply one — a photo/scan
   of the plan, or a link to it. Providing it marks the survey available and
   tells the studio. */
function SurveyPrompt({ p }: { p: any }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  if (p.surveyDoc) {
    return (
      <div className="card reveal" style={{ padding: "13px 18px", marginBottom: 18, background: "color-mix(in srgb, var(--ok) 6%, var(--card))", borderColor: "color-mix(in srgb,var(--ok) 35%,var(--line))" }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <div className="row" style={{ gap: 10 }}>
            <Icon.checkCircle width={18} style={{ color: "var(--ok)", flexShrink: 0 }} />
            <span style={{ fontSize: 13.5 }}>Thanks — we have your survey / site plan{p.surveyDoc.name ? <>: <b>{p.surveyDoc.name}</b></> : ""}.</span>
          </div>
          {p.surveyDoc.url && <a className="btn ghost sm" href={p.surveyDoc.url} target="_blank" rel="noreferrer"><Icon.arrowUpRight width={13} />View</a>}
        </div>
      </div>
    );
  }
  if (p.surveyPlan !== "no") return null;

  const [err, setErr] = useState<string | null>(null);
  async function onFile(files: FileList | null) {
    const f = files?.[0];
    if (!f) return;
    setBusy(true); setErr(null);
    try {
      // Photos are still compressed; PDFs and other files stream straight to R2.
      if (f.type.startsWith("image/") && f.size < 8 * 1024 * 1024) {
        const src = await storeImage(await fileToDataUri(f), p.client.code);
        actions.provideSurvey(p.id, { name: f.name, url: src });
      } else {
        const { url, name } = await storeFile(f, p.client.code);
        actions.provideSurvey(p.id, { name, url });
      }
    } catch (e: any) {
      setErr(e?.message === "file storage not configured" ? "File uploads aren't switched on yet — paste a link instead." : "That didn't upload. Try a photo, or paste a link.");
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  }
  function saveLink() {
    const url = link.trim();
    if (!url) return;
    actions.provideSurvey(p.id, { name: "Survey link", url });
    setLink(""); setLinkOpen(false);
  }

  return (
    <div className="card reveal" style={{ padding: "16px 20px", marginBottom: 18, borderColor: "color-mix(in srgb, var(--warn) 45%, var(--line))", background: "color-mix(in srgb, var(--warn) 5%, var(--card))" }}>
      <div className="row" style={{ gap: 10, marginBottom: 8 }}>
        <Icon.brief width={18} style={{ color: "var(--warn)", flexShrink: 0 }} />
        <div style={{ fontWeight: 600, fontSize: 15 }}>We need a survey or site plan</div>
      </div>
      <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6, marginBottom: 12 }}>
        There's no survey on file for your site. If you have one — a measured survey, architect's plan, or even a clear drawing with dimensions — please share it so we design to the real site rather than having to measure it first. A photo or a PDF both work.
      </p>
      <div className="row wrap" style={{ gap: 10 }}>
        <button className="btn accent sm" onClick={() => fileRef.current?.click()} disabled={busy}><Icon.image width={14} />{busy ? "Uploading…" : "Upload a plan"}</button>
        <button className="btn ghost sm" onClick={() => setLinkOpen((v) => !v)}><Icon.link width={14} />Add a link</button>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => onFile(e.target.files)} />
      </div>
      {linkOpen && (
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <input className="input" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" onKeyDown={(e) => e.key === "Enter" && saveLink()} style={{ flex: 1 }} />
          <button className="btn accent sm" onClick={saveLink} disabled={!link.trim()}>Share</button>
        </div>
      )}
      {err && <div style={{ color: "var(--warn)", fontSize: 12, marginTop: 8 }}>{err}</div>}
      <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>Don't have one? Leave it — we'll arrange a measured survey, and the cost is set out in your engagement.</div>
    </div>
  );
}

function BriefTab({ p }: { p: any }) {
  const b = p.brief;
  const [note, setNote] = useState("");
  const [refOpen, setRefOpen] = useState(false);
  const [draft, setDraft] = useState<any[]>(() => b.sections.map((s: any) => ({ ...s })));
  const [savedTick, setSavedTick] = useState(false);
  const editable = b.status === "draft" || b.status === "changes"; // intake mode
  const actionable = b.status === "sent";
  const refs = b.references ?? [];
  // The accommodation is always the client's to shape. Once the brief is signed
  // off it stays editable, but a change then is a variation — say so plainly
  // rather than quietly locking them out.
  const spacesEditable = true;
  const spacesLocked = b.status === "approved";

  function saveInput() { actions.updateBrief(p.id, draft); setSavedTick(true); setTimeout(() => setSavedTick(false), 2500); }
  const patchSpace = (id: string, patch: any) => actions.setBriefSpaces(p.id, (b.spaces ?? []).map((x: any) => x.id === id ? { ...x, ...patch } : x));
  const removeSpace = (id: string) => actions.setBriefSpaces(p.id, (b.spaces ?? []).filter((x: any) => x.id !== id));
  const addSpace = () => actions.setBriefSpaces(p.id, [...(b.spaces ?? []), newSpace()]);

  return (
    <div style={{ maxWidth: 760 }}>
      <div className="row between reveal" style={{ marginBottom: 8 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>Your design brief</h2>
        <StatusBadge status={b.status} />
      </div>

      {editable && <p className="muted reveal" style={{ marginBottom: 18, maxWidth: 560 }}>Help us understand your project. Fill in as much as you like — there are no wrong answers, and you can come back any time.</p>}
      {b.status === "approved" && <div className="card reveal" style={{ padding: "16px 20px", marginBottom: 18, background: "color-mix(in srgb, var(--ok) 7%, var(--card))", borderColor: "color-mix(in srgb,var(--ok) 35%,var(--line))" }}><div className="row" style={{ gap: 10 }}><Icon.checkCircle width={20} style={{ color: "var(--ok)" }} /><span>You approved this brief on {fmtDate(b.approvedOn)}.</span></div></div>}

      {/* the approved brief tracked against the design, once the studio shares it */}
      <ClientBriefTracker p={p} />
      {p.briefChecklist?.sharedWithClient && (p.brief.spaces ?? []).length > 0 && (
        <div className="row reveal" style={{ justifyContent: "flex-end", marginTop: -8, marginBottom: 18 }}>
          <button className="btn soft sm" onClick={() => void downloadSpaceReportPdf(p)}><Icon.download width={13} />Download the space report (PDF)</button>
        </div>
      )}

      <SurveyPrompt p={p} />

      <div className="col reveal" style={{ gap: 12 }}>
        {(editable ? draft : b.sections).map((s: any, i: number) => (
          <div key={s.id} className="card pad">
            <div className="kicker">{String(i + 1).padStart(2, "0")} · {s.title}</div>
            {editable ? (
              <textarea className="textarea" style={{ marginTop: 10 }} value={s.body} placeholder={s.hint || "…"}
                onChange={(e) => setDraft((d) => d.map((x) => x.id === s.id ? { ...x, body: e.target.value } : x))} />
            ) : (
              s.body?.trim()
                ? <p style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.6 }}>{s.body}</p>
                : <p className="dim" style={{ marginTop: 8, fontSize: 14, fontStyle: "italic" }}>{s.hint || "Not yet filled in."}</p>
            )}
          </div>
        ))}
      </div>

      {editable && (
        <div className="row reveal" style={{ gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
          {savedTick && <span className="badge ok"><Icon.check width={13} />Saved — thank you</span>}
          <button className="btn accent" onClick={saveInput}><Icon.check width={15} />Save my input</button>
        </div>
      )}

      {/* accommodation — the client shapes this until they approve the brief */}
      <div className="reveal" style={{ marginTop: 30 }}>
        <div className="row between wrap" style={{ marginBottom: 10, gap: 10 }}>
          <h3 className="serif" style={{ fontSize: 22 }}>Your spaces</h3>
          <div className="row" style={{ gap: 10 }}>
            <span className="dim" style={{ fontSize: 13 }}>{totalArea(b.spaces).toLocaleString()} {unitLabel(b.areaUnit)} total</span>
            {spacesEditable && <button className="btn ghost sm" onClick={addSpace}><Icon.plus width={14} />Add a space</button>}
          </div>
        </div>
        {spacesLocked
          ? <div className="panel" style={{ padding: "10px 13px", marginBottom: 12, fontSize: 12.5, lineHeight: 1.5, borderStyle: "dashed" }}>
              You approved this brief on {fmtDate(b.approvedOn)}, so the accommodation below is what we're designing to. You can still change it — just note that a change after sign-off is treated as a design variation, at the rate in your engagement.
            </div>
          : <p className="muted" style={{ fontSize: 13.5, marginBottom: 12, maxWidth: 560 }}>Shape the accommodation to suit you — change a size, add a room, or take one out. We design from this.</p>}
        {(b.spaces ?? []).length === 0 ? (
          <div className="panel" style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>
            No spaces yet.{spacesEditable ? " Add the rooms you need and we'll take it from there." : ""}
          </div>
        ) : (
          <div className="card" style={{ overflow: "hidden" }}>
            {spacesEditable && (
              <div className="sp-grid" style={{ padding: "10px 16px", borderBottom: "1px solid var(--line)" }}>
                <span className="kicker" style={{ fontSize: 9 }}>Space</span>
                <span className="kicker" style={{ fontSize: 9, textAlign: "center" }}>Qty</span>
                <span className="kicker" style={{ fontSize: 9, textAlign: "right" }}>Each</span>
                <span className="kicker" style={{ fontSize: 9, textAlign: "right" }}>Subtotal</span>
                <span />
              </div>
            )}
            {b.spaces.map((s: any, i: number) => spacesEditable ? (
              <div key={s.id} className="sp-grid" style={{ padding: "9px 16px", borderTop: i ? "1px solid var(--line)" : "none", alignItems: "center" }}>
                <input className="input" value={s.name} placeholder="Room name" onChange={(e) => patchSpace(s.id, { name: e.target.value })} style={{ padding: "7px 10px", fontSize: 13.5 }} />
                <input className="input mono" type="number" min={1} value={s.qty} onChange={(e) => patchSpace(s.id, { qty: Math.max(1, Number(e.target.value) || 1) })} style={{ padding: "7px 6px", fontSize: 13, textAlign: "center" }} />
                <input className="input mono" type="number" min={0} step={10} value={s.area} onChange={(e) => patchSpace(s.id, { area: Math.max(0, Number(e.target.value) || 0) })} style={{ padding: "7px 6px", fontSize: 13, textAlign: "right" }} />
                <span className="mono dim" style={{ fontSize: 13, textAlign: "right" }}>{(s.qty * s.area).toLocaleString()}</span>
                <button className="iconbtn" style={{ width: 28, height: 28 }} title={`Remove ${s.name || "space"}`} onClick={() => removeSpace(s.id)}><Icon.close width={12} /></button>
              </div>
            ) : (
              <div key={s.id} className="row between" style={{ padding: "11px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <span style={{ fontSize: 14 }}>{s.qty > 1 && <span className="mono dim" style={{ marginRight: 6 }}>{s.qty}×</span>}{s.name}</span>
                <span className="mono dim" style={{ fontSize: 13 }}>{(s.qty * s.area).toLocaleString()} {unitLabel(b.areaUnit)}</span>
              </div>
            ))}
          </div>
        )}
        <style>{`
          .sp-grid { display: grid; grid-template-columns: 1fr 58px 84px 84px 32px; gap: 10px; }
          @media (max-width: 560px){ .sp-grid { grid-template-columns: 1fr 46px 68px 0 30px; } .sp-grid > :nth-child(4){ display:none } }
        `}</style>
      </div>

      {/* references — client can contribute */}
      <div className="row between reveal" style={{ margin: "30px 0 14px" }}>
        <h3 className="serif" style={{ fontSize: 22 }}>Design references</h3>
        {b.status !== "approved" && <button className="btn ghost sm" onClick={() => setRefOpen(true)}><Icon.plus width={14} />Add a reference</button>}
      </div>
      {refs.length === 0 ? (
        <div className="panel reveal" style={{ padding: 26, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>Share images or links to anything you love — rooms, materials, moods.</div>
      ) : (
        <div className="reveal" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px,1fr))", gap: 14 }}>
          {refs.map((r: any) => (
            <div key={r.id} className="card hoverable" style={{ overflow: "hidden" }}>
              <Plate hue={r.hue} h={110} />
              <div style={{ padding: 13 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.title}</div>
                {r.note && <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>{r.note}</div>}
                {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="row" style={{ gap: 5, fontSize: 12, color: "var(--accent-ink)", marginTop: 6 }}><Icon.arrowUpRight width={13} />View</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      {actionable && (
        <div className="card pad reveal" style={{ marginTop: 24 }}>
          <div className="serif" style={{ fontSize: 20 }}>Ready to proceed?</div>
          <textarea className="textarea" style={{ marginTop: 12 }} placeholder="Add a note for the studio (optional)…" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="row" style={{ gap: 10, marginTop: 12 }}>
            <button className="btn accent" onClick={() => actions.approveBrief(p.id, note)}><Icon.check width={16} />Approve brief</button>
            <button className="btn ghost" onClick={() => note.trim() && actions.requestBriefChanges(p.id, note)}>Request changes</button>
          </div>
        </div>
      )}

      <AddReference open={refOpen} onClose={() => setRefOpen(false)} onAdd={(r) => { actions.addReference(p.id, r, "client"); setRefOpen(false); }} />
    </div>
  );
}

/* --------------------------------------------------------------- Configure */
function ConfigureTab({ p, go }: { p: any; go: (t: Tab) => void }) {
  const [sent, setSent] = useState(false);
  const rc = useRateCard();
  const config = p.estimate ?? defaultConfig(p.budget, p.currency);
  const lock = p.feeLock;
  function request() {
    const est = computeEstimate(config, rc);
    const list = est.lines.map((l: any) => `• ${l.label} — ${money(l.amount, config.currency)}`).join("\n");
    actions.addFeedback(p.id, "client", p.client.name, "Scope & fee",
      `I'd like to proceed with this scope (est. ${money(est.total, config.currency)}):\n${list}`);
    setSent(true); setTimeout(() => setSent(false), 3000);
  }
  /* Once the studio locks it, this is a record of what was agreed rather than a
     thing to change — everything stays visible, nothing stays editable. */
  return (
    <div>
      <div className="reveal" style={{ marginBottom: 20 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>{lock ? "Your agreed scope" : "Configure your project"}</h2>
        <p className="muted" style={{ marginTop: 8, maxWidth: 560 }}>
          {lock
            ? "This is the scope and fee agreed with the studio. It's here in full for your records — to change anything, just send a message and we'll take it from there."
            : "Set your construction budget and choose the disciplines and services you'd like. The design fee updates as you go — when it feels right, send it to the studio."}
        </p>
      </div>
      {lock && (
        <div className="card reveal" style={{ padding: "12px 16px", marginBottom: 16, background: "color-mix(in srgb, var(--ok) 8%, transparent)" }}>
          <div className="row" style={{ gap: 9 }}>
            <Icon.check width={16} style={{ color: "var(--ok)", flexShrink: 0 }} />
            <span style={{ fontSize: 13.5 }}>
              <b>Agreed and locked{lock.on ? ` on ${fmtDate(lock.on)}` : ""}.</b>
              {lock.note ? ` ${lock.note}` : ""}
            </span>
          </div>
        </div>
      )}
      <div className="reveal">
        <ScopeConfigurator config={config} readOnly={!!lock}
          lockedNote={lock ? "This is the agreed scope — shown in full, no longer editable." : undefined}
          onChange={lock ? undefined : (c) => actions.setEstimate(p.id, c)}
          footer={lock
            ? <button className="btn ghost block lg" onClick={() => go("messages")}><Icon.chat width={16} />Message the studio about the scope</button>
            : <button className="btn accent block lg" onClick={request} disabled={sent}>
                {sent ? <><Icon.check width={16} />Sent to the studio</> : <><Icon.send width={16} />Send this scope to the studio</>}
              </button>} />
      </div>
    </div>
  );
}

/* --------------------------------------------------------------- Moodboard */
function MoodboardTab({ p }: { p: any }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const mb = p.moodboard ?? { images: [], swatches: [] };
  const interior = isInterior(p.type);

  async function onFiles(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true);
    for (const f of Array.from(files).slice(0, 12)) {
      if (!f.type.startsWith("image/")) continue;
      try { const src = await storeImage(await fileToDataUri(f), p.client.code); actions.addMoodImage(p.id, src, "", "client", p.client.name); } catch {}
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <div>
      <div className="row between wrap reveal" style={{ marginBottom: 8, gap: 12 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>{interior ? "Your interior mood board" : "Your mood board"}</h2>
        <button className="btn accent" onClick={() => fileRef.current?.click()} disabled={busy}><Icon.image width={16} />{busy ? "Adding…" : "Share images"}</button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => onFiles(e.target.files)} />
      </div>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 560 }}>Share any images you're drawn to — rooms, materials, light, moods. We'll develop them into the feature palette for your {interior ? "interiors" : "project"}.</p>

      {mb.images.length === 0 ? (
        <div className="panel reveal" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>
          <Icon.image width={26} style={{ opacity: .5 }} /><div style={{ marginTop: 8 }}>Add your first images — anything that captures the feeling you're after.</div>
        </div>
      ) : (
        <div className="reveal" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(170px,1fr))", gap: 14 }}>
          {mb.images.map((im: any) => (
            <div key={im.id} className="card hoverable" style={{ overflow: "hidden" }}>
              <div style={{ position: "relative", aspectRatio: "4/3", background: "var(--paper-2)" }}>
                <img src={im.src} alt={im.caption || "mood"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                {im.addedBy === "client" && <button className="iconbtn" style={{ position: "absolute", top: 8, right: 8, width: 28, height: 28, background: "rgba(255,255,255,.85)" }} onClick={() => actions.removeMoodImage(p.id, im.id)}><Icon.close width={13} /></button>}
              </div>
              <div style={{ padding: "8px 12px", fontSize: 11.5 }} className="dim">{im.addedBy === "client" ? "You" : im.addedByName}</div>
            </div>
          ))}
        </div>
      )}

      {/* studio's developed palette */}
      {mb.swatches.length > 0 && (
        <div className="reveal" style={{ marginTop: 34 }}>
          <div className="kicker" style={{ marginBottom: 4 }}>Developed by the studio</div>
          <h3 className="serif" style={{ fontSize: 22, marginBottom: 14 }}>Your feature palette</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 14 }}>
            {mb.swatches.map((s: any) => (
              <div key={s.id} className="panel" style={{ overflow: "hidden", padding: 0 }}>
                <div style={{ height: 64, background: s.hex }} />
                <div style={{ padding: "9px 12px" }}><div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div><div className="dim" style={{ fontSize: 11 }}>{s.material || ""}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------- Reviews */
function ReviewsTab({ p }: { p: any }) {
  const [reply, setReply] = useState<Record<string, string>>({});
  const [attach, setAttach] = useState<Record<string, string>>({});   // pending comment image per sheet
  const [upBusy, setUpBusy] = useState<string | null>(null);
  const shared = (p.reviews ?? []).filter((r: any) => r.sharedWithClient);
  function send(rid: string, sheetId: string) {
    const body = (reply[sheetId] ?? "").trim();
    const img = attach[sheetId];
    if (!body && !img) return;
    actions.addRemark(p.id, rid, sheetId, "client", p.client.name, body || "(see attached mark-up)", img);
    setReply((x) => ({ ...x, [sheetId]: "" }));
    setAttach((x) => { const n = { ...x }; delete n[sheetId]; return n; });
  }
  async function attachFile(sheetId: string, f: File | null) {
    if (!f) return;
    setUpBusy(sheetId);
    try { const src = await storeImage(await fileToDataUri(f), p.client.code); setAttach((x) => ({ ...x, [sheetId]: src })); }
    catch { /* upload off */ }
    finally { setUpBusy(null); }
  }
  return (
    <div>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Drawing reviews</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 560 }}>Mark up the drawings sheet by sheet — leave a remark and we'll revise and reissue. Your notes go straight to the design team.</p>
      {shared.length === 0 && <div className="panel reveal" style={{ padding: 30, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>No drawings shared for review yet.</div>}
      {shared.map((r: any) => (
        <div key={r.id} className="reveal" style={{ marginBottom: 30 }}>
          <div className="row between" style={{ marginBottom: 12 }}><h3 className="serif" style={{ fontSize: 22 }}>{r.title}</h3><StatusBadge status={r.status === "open" ? "active" : "done"} /></div>
          {/* your change requests and how each was addressed — accept the revised implementations */}
          <ChangeRegister r={r} mode="client" onAccept={(sheetId, remarkId, accept) => actions.acceptRemarkResponse(p.id, r.id, sheetId, remarkId, p.client.name, accept)} />
          <div className="col" style={{ gap: 16 }}>
            {r.sheets.map((s: any) => (
              <div key={s.id} className="card" style={{ overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "220px 1fr" }} className="pr-sheet">
                  <div style={{ borderRight: "1px solid var(--line)", position: "relative", minHeight: 190 }}>
                    {s.url
                      ? <img src={s.url} alt={`${s.code} ${s.title}`} style={{ width: "100%", height: "100%", minHeight: 190, objectFit: "cover", display: "block" }} />
                      : <Plate hue={s.hue} h={190} label={`${s.code} · ${s.revision}`} />}
                    {s.url && <span className="badge" style={{ position: "absolute", top: 10, left: 10, background: "rgba(255,255,255,.9)", color: "var(--ink)" }}>{s.code} · {s.revision}</span>}
                  </div>
                  <div style={{ padding: 18 }}>
                    <div className="row" style={{ gap: 8 }}><span className="mono dim">{s.code}</span><span className="badge" style={{ fontSize: 10 }}>{s.discipline}</span><span className="badge accent" style={{ fontSize: 10 }}>rev {s.revision}</span></div>
                    <h4 className="serif" style={{ fontSize: 18, marginTop: 6 }}>{s.title}</h4>
                    {s.summary && <div className="panel" style={{ padding: "9px 12px", marginTop: 10, fontSize: 12.5, lineHeight: 1.55 }}><span className="kicker" style={{ fontSize: 8.5 }}>What changed{s.revisedOn ? ` · ${relative(s.revisedOn)}` : ""}</span><div style={{ marginTop: 3 }}>{s.summary}</div></div>}
                    <div className="col" style={{ gap: 8, marginTop: 12 }}>
                      {s.remarks.map((m: any) => (
                        <div key={m.id} className="panel" style={{ padding: "9px 12px", opacity: m.resolved && !m.response ? 0.6 : 1 }}>
                          <div className="row" style={{ gap: 8 }}><span style={{ fontSize: 12.5, fontWeight: 600 }}>{m.authorName}</span>{m.author === "team" && <span className="badge" style={{ fontSize: 9 }}>studio</span>}<span className="dim" style={{ fontSize: 11 }}>{relative(m.createdOn)}</span>{m.response ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />addressed</span> : m.resolved && <span className="badge ok" style={{ fontSize: 9 }}>done</span>}</div>
                          <div style={{ fontSize: 14, marginTop: 4 }}>{m.body}</div>
                          {m.attachmentUrl && <a href={m.attachmentUrl} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 6 }}><img src={m.attachmentUrl} alt="mark-up" style={{ width: 120, height: 84, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} /></a>}
                          {m.response && (
                            <div style={{ marginTop: 9, paddingTop: 9, borderTop: "1px dashed var(--line-2)" }}>
                              <div className="row between" style={{ gap: 8, marginBottom: 5 }}>
                                <span className="row" style={{ gap: 8 }}><Icon.check width={12} style={{ color: "var(--ok)" }} /><span className="kicker" style={{ fontSize: 8.5 }}>Revised implementation{m.response.revision ? ` · rev ${m.response.revision}` : ""}</span></span>
                                {m.response.accepted
                                  ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />You approved this</span>
                                  : <button className="btn accent sm" onClick={() => actions.acceptRemarkResponse(p.id, r.id, s.id, m.id, p.client.name, true)}><Icon.check width={12} />Accept</button>}
                              </div>
                              <div style={{ fontSize: 13.5 }}>{m.response.body}</div>
                              {m.response.url && <img src={m.response.url} alt="revised implementation" style={{ width: 140, height: 96, objectFit: "cover", borderRadius: 8, marginTop: 8, border: "1px solid var(--line)" }} />}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {attach[s.id] && (
                      <div className="row" style={{ gap: 8, marginTop: 10, alignItems: "center" }}>
                        <img src={attach[s.id]} alt="attachment" style={{ width: 56, height: 40, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} />
                        <span className="dim" style={{ fontSize: 12 }}>Mark-up attached</span>
                        <button className="iconbtn" style={{ width: 26, height: 26 }} title="Remove" onClick={() => setAttach((x) => { const n = { ...x }; delete n[s.id]; return n; })}><Icon.close width={12} /></button>
                      </div>
                    )}
                    <div className="row" style={{ gap: 8, marginTop: 12 }}>
                      <label className="iconbtn" style={{ width: 40, flexShrink: 0, cursor: upBusy === s.id ? "default" : "pointer" }} title="Attach a marked-up image / photo">
                        {upBusy === s.id ? <span className="dot" /> : <Icon.download width={15} style={{ transform: "rotate(180deg)" }} />}
                        <input type="file" accept="image/*" hidden disabled={upBusy === s.id} onChange={(e) => attachFile(s.id, e.target.files?.[0] ?? null)} />
                      </label>
                      <input className="input" placeholder="Request a change on this sheet…" value={reply[s.id] ?? ""} onChange={(e) => setReply((x) => ({ ...x, [s.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && send(r.id, s.id)} />
                      <button className="btn accent" onClick={() => send(r.id, s.id)}><Icon.send width={15} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      <style>{`@media (max-width:640px){ .pr-sheet{ grid-template-columns:1fr !important } }`}</style>
    </div>
  );
}

/* ------------------------------------------------------------------- RFIs */
function ClientRfisTab({ p }: { p: any }) {
  const [subject, setSubject] = useState("");
  const [question, setQuestion] = useState("");
  const [ans, setAns] = useState<Record<string, string>>({});
  const rfis = [...(p.rfis ?? [])].sort((a: any, b: any) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  function raise() { if (!subject.trim() || !question.trim()) return; actions.createRfi(p.id, { subject, question, by: p.client.name, role: "client" }); setSubject(""); setQuestion(""); }
  return (
    <div style={{ maxWidth: 820 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Requests for Information</h2>
      <p className="muted reveal" style={{ marginBottom: 20, maxWidth: 560 }}>Ask the design team a formal question during the works — each one is numbered, tracked and answered.</p>
      <div className="card pad reveal" style={{ marginBottom: 22 }}>
        <div className="kicker" style={{ marginBottom: 8 }}>Raise an RFI</div>
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject — e.g. Tiling to wet areas" style={{ marginBottom: 8 }} />
        <textarea className="input" rows={3} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Your question…" />
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 10 }}><button className="btn accent" disabled={!subject.trim() || !question.trim()} onClick={raise}><Icon.send width={15} />Submit RFI</button></div>
      </div>
      {rfis.length === 0 && <div className="panel reveal" style={{ padding: 26, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>No RFIs yet.</div>}
      <div className="col" style={{ gap: 12 }}>
        {rfis.map((r: any) => (
          <div key={r.id} className="card pad reveal">
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}><span className="badge accent mono" style={{ fontSize: 11 }}>{r.ref}</span><span style={{ fontSize: 15, fontWeight: 600 }}>{r.subject}</span></span>
              <span className="badge" style={{ fontSize: 9 }}>{r.status === "answered" ? "Answered" : r.status === "closed" ? "Closed" : "Open"}</span>
            </div>
            <div style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.55 }}>{r.question}</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 6 }}>{[`by ${r.raisedBy}`, relative(r.raisedOn), r.dueOn && `reply by ${fmtDate(r.dueOn)}`].filter(Boolean).join(" · ")}</div>
            {r.answer && <div className="panel" style={{ padding: "10px 12px", marginTop: 10, background: "color-mix(in srgb, var(--sky) 6%, var(--card))" }}><div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Answer · {r.answeredBy}{r.answeredOn ? ` · ${fmtDate(r.answeredOn)}` : ""}</div><div style={{ fontSize: 13.5, lineHeight: 1.55 }}>{r.answer}</div></div>}
            {r.status !== "closed" && r.ballInCourt === "client" && (
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <input className="input" placeholder="Your response…" value={ans[r.id] ?? ""} onChange={(e) => setAns((x) => ({ ...x, [r.id]: e.target.value }))} />
                <button className="btn accent" disabled={!(ans[r.id] ?? "").trim()} onClick={() => { actions.answerRfi(p.id, r.id, ans[r.id], p.client.name); setAns((x) => ({ ...x, [r.id]: "" })); }}><Icon.send width={15} /></button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- Film */
/* The studio's motion work, playing in the portal rather than sent as a link.
   One film gets the whole room; several stack, newest first. */
function FilmTab({ p }: { p: any }) {
  const films = (p.films ?? []).filter((f: any) => f.sharedWithClient);
  /* Someone following an emailed link after the film came back down. Say so —
     a blank page reads as broken. */
  if (!films.length) return (
    <div className="reveal">
      <h2 className="serif" style={{ fontSize: 30, marginBottom: 8 }}>Film</h2>
      <p className="muted" style={{ maxWidth: 520, lineHeight: 1.7 }}>
        This film isn't in your portal at the moment — {STUDIO.name} may be revising it. Ask us and we'll put it back up.
      </p>
    </div>
  );
  const solo = films.length === 1;
  return (
    <div className="reveal">
      {/* The film is the content. A heading and a subtitle are enough around it —
          anything more and the client is reading instead of watching. */}
      <h2 className="serif" style={{ fontSize: 30, marginBottom: solo && films[0].subtitle ? 4 : 20 }}>{solo ? films[0].title : "Film"}</h2>
      {solo && films[0].subtitle && (
        <p className="muted" style={{ maxWidth: 620, lineHeight: 1.7, marginBottom: 22 }}>{films[0].subtitle}</p>
      )}
      <div className="col" style={{ gap: 24 }}>
        {[...films].reverse().map((f: any) => <FilmCard key={f.id} film={f} height={solo ? "clamp(420px, 72vh, 780px)" : "clamp(340px, 56vh, 600px)"} />)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Budget & size */
/* The client's side of the sizing conversation: move the budget, see what it
   buys, see what fits. The rate is ours; the decision is theirs. */
function ClientBudgetTab({ p }: { p: any }) {
  const locked = !!p.costBrief?.locked;
  return (
    <div style={{ maxWidth: 860 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Budget &amp; size</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 620 }}>
        What you can build for a given budget, at our construction rates — and honestly, what fits inside it. Move the
        budget and everything below moves with it. This is for sizing the project, not a builder's quotation.
      </p>
      {locked && (
        <div className="panel reveal" style={{ padding: "11px 14px", marginBottom: 18, borderLeft: "3px solid var(--ok)" }}>
          <div className="row" style={{ gap: 8 }}><Icon.check width={14} style={{ color: "var(--ok)" }} /><b style={{ fontSize: 13.5 }}>This budget is agreed</b></div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.55 }}>It's the basis of the design fee now, so it's fixed here. If it needs to change, message us and we'll reopen it.</div>
        </div>
      )}
      <BudgetStudio p={p} mode="client" />
    </div>
  );
}

/* ------------------------------------------------------------- The story */
/* The project as a narrative rather than a database — what happened, in order,
   with the pictures. Something a client can scroll and forward to their board. */
function ClientStoryTab({ p }: { p: any }) {
  type Beat = { on: string; title: string; body?: string; kind: string; images?: string[] };
  const beats: Beat[] = [];

  beats.push({ on: p.createdOn, title: "Project opened", body: `${STUDIO.name} appointed on ${p.name}.`, kind: "start" });
  for (const s of ((p as any).stages ?? [])) {
    if (s.status === "done" && s.completedOn) beats.push({ on: s.completedOn, title: `${s.label} complete`, body: s.blurb, kind: "stage" });
    else if (s.status === "active" && s.startedOn) beats.push({ on: s.startedOn, title: `${s.label} began`, body: s.blurb, kind: "stage" });
  }
  if (p.brief?.status === "approved" && p.brief.approvedOn) beats.push({ on: p.brief.approvedOn, title: "Brief agreed", body: "The design brief was approved and became the basis of the work.", kind: "brief" });
  if (p.engagement?.signedOn) beats.push({ on: p.engagement.signedOn, title: "Engagement signed", body: "The appointment was formally accepted.", kind: "sign" });
  if (p.conceptsPresentedOn) beats.push({ on: p.conceptsPresentedOn, title: "Design directions presented", images: (p.concepts ?? []).map((c: any) => c.url).filter(Boolean).slice(0, 3), kind: "design" });
  for (const a of (p.approvals ?? [])) if (a.status === "signed" && a.signedOn) beats.push({ on: a.signedOn, title: `${a.title} approved`, body: `${a.sheets.length} sheet${a.sheets.length === 1 ? "" : "s"} signed off.`, kind: "approve" });
  for (const s of (p.drawingSets ?? [])) if (s.sharedWithClient) beats.push({ on: s.issuedOn, title: `${s.label} issued`, body: `${s.sheets.length} sheets issued as a numbered set.`, kind: "drawings" });
  for (const u of (p.siteLog ?? [])) beats.push({ on: u.on, title: u.title, body: u.note ?? (typeof u.percent === "number" ? `${u.percent}% complete${u.zone ? ` · ${u.zone}` : ""}` : undefined), images: (u.photos ?? []).slice(0, 3), kind: "site" });
  for (const r of (p.reports ?? [])) if (r.status === "issued" && r.issuedOn) beats.push({ on: r.issuedOn, title: r.title || "Progress report issued", kind: "report" });

  const sorted = beats.filter((b) => b.on).sort((a, b) => +new Date(b.on) - +new Date(a.on));
  const HUE: Record<string, string> = { start: "var(--ink-3)", stage: "var(--accent)", brief: "var(--sage)", sign: "var(--ok)", design: "var(--gold)", approve: "var(--ok)", drawings: "var(--sky)", site: "var(--accent)", report: "var(--ink-3)" };

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>The story so far</h2>
      <p className="muted reveal" style={{ marginBottom: 26, maxWidth: 560 }}>
        Your project in order — what was agreed, what was drawn, what's been built. Everything here already happened; nothing is a projection.
      </p>
      {sorted.length === 0 ? (
        <div className="panel reveal" style={{ padding: 30, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>The story starts once the first milestone lands.</div>
      ) : (
        <div style={{ position: "relative", paddingLeft: 26 }}>
          <div style={{ position: "absolute", left: 7, top: 8, bottom: 8, width: 1.5, background: "var(--line-2)" }} />
          <div className="col" style={{ gap: 22 }}>
            {sorted.map((b, i) => (
              <div key={i} className="reveal" style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: -25, top: 5, width: 11, height: 11, borderRadius: 11, background: HUE[b.kind] ?? "var(--ink-3)", border: "2px solid var(--paper)" }} />
                <div className="dim" style={{ fontSize: 11.5 }}>{fmtDate(b.on)} · {relative(b.on)}</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{b.title}</div>
                {b.body && <div className="muted" style={{ fontSize: 13.5, marginTop: 4, lineHeight: 1.6 }}>{b.body}</div>}
                {b.images && b.images.length > 0 && (
                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, b.images.length)},1fr)`, gap: 8, marginTop: 10 }}>
                    {b.images.map((src, j) => <img key={j} src={src} alt="" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, border: "1px solid var(--line)" }} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- Approvals */
function ClientApprovalsTab({ p }: { p: any }) {
  const [signing, setSigning] = useState<any | null>(null);
  const list = [...(p.approvals ?? [])].sort((a: any, b: any) => +new Date(b.issuedOn) - +new Date(a.issuedOn));
  return (
    <div style={{ maxWidth: 820 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Drawing approvals</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 580 }}>
        Where we ask you to formally sign off a set of drawings. Signing confirms you're happy for us to proceed on that basis — it isn't a payment, and it doesn't waive anything you've already raised.
      </p>
      <div className="col" style={{ gap: 14 }}>
        {list.map((a: any) => (
          <div key={a.id} className="card pad reveal" style={{ borderLeft: `3px solid ${a.status === "signed" ? "var(--ok)" : a.status === "declined" ? "var(--danger)" : "var(--warn)"}` }}>
            <div className="row between wrap" style={{ gap: 10 }}>
              <span className="row" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}>
                <span className="badge accent mono" style={{ fontSize: 10.5 }}>{a.ref}</span>
                <span style={{ fontSize: 16, fontWeight: 600 }}>{a.title}</span>
              </span>
              <span className="badge" style={{ fontSize: 9, color: a.status === "signed" ? "var(--ok)" : a.status === "declined" ? "var(--danger)" : undefined }}>
                {a.status === "signed" ? "Signed" : a.status === "declined" ? "Declined" : "Awaiting your signature"}
              </span>
            </div>
            {a.note && <div style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.6 }}>{a.note}</div>}
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>{a.sheets.length} sheet{a.sheets.length === 1 ? "" : "s"} · issued {fmtDate(a.issuedOn)}{a.signedOn ? ` · ${a.status === "signed" ? "signed" : "declined"} ${fmtDate(a.signedOn)}` : ""}</div>
            <div className="col" style={{ gap: 2, marginTop: 10, maxHeight: 200, overflowY: "auto" }}>
              {a.sheets.map((s: any, i: number) => (
                <div key={i} className="row between" style={{ padding: "5px 9px", borderRadius: 6, background: "var(--paper-2)", gap: 10 }}>
                  <span className="row" style={{ gap: 8, minWidth: 0 }}><span className="mono dim" style={{ fontSize: 11 }}>{s.code}</span><span style={{ fontSize: 12.5 }}>{s.title}</span></span>
                  <span className="mono dim" style={{ fontSize: 11 }}>{s.revision ?? ""}</span>
                </div>
              ))}
            </div>
            {a.signatureDataUri && <img src={a.signatureDataUri} alt="your signature" style={{ height: 46, marginTop: 10, background: "#fff", borderRadius: 6, border: "1px solid var(--line)", padding: 4 }} />}
            {a.declineNote && <div style={{ fontSize: 13, marginTop: 8, color: "var(--danger)" }}>{a.declineNote}</div>}
            {a.status !== "issued" && <div style={{ marginTop: 10 }}><RecordBadge p={p} kind={a.status === "signed" ? "approval-signed" : "approval-declined"} refId={a.id} title={`${a.ref} — ${a.title}`} by={a.signedBy ?? p.client.name} compact /></div>}
            {a.status === "issued" && (
              <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
                <button className="btn accent" onClick={() => setSigning(a)}><Icon.sign width={15} />Review &amp; sign</button>
                <button className="btn ghost" onClick={() => { const note = prompt("What needs to change before you can approve?") ?? undefined; if (note !== undefined) actions.declineApproval(p.id, a.id, p.client.name, note || undefined); }}>Not yet — I have comments</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {signing && <SignApprovalModal p={p} approval={signing} onClose={() => setSigning(null)} />}
    </div>
  );
}

function SignApprovalModal({ p, approval, onClose }: { p: any; approval: any; onClose: () => void }) {
  const [name, setName] = useState(p.client.name ?? "");
  const [agree, setAgree] = useState(false);
  const [sig, setSig] = useState<string | null>(null);
  return (
    <Modal open onClose={onClose} title={`Sign ${approval.ref}`} wide>
      <div className="col" style={{ gap: 14 }}>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          You're approving <b>{approval.sheets.length} sheet{approval.sheets.length === 1 ? "" : "s"}</b> — {approval.title}. This records your sign-off with today's date; it doesn't authorise any payment.
        </p>
        <div className="field"><label>Your name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label>Signature</label><SignaturePad onChange={setSig} /></div>
        <label className="row" style={{ gap: 9, fontSize: 13.5, cursor: "pointer" }}>
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          I approve these drawings for the studio to proceed.
        </label>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={!agree || !name.trim()} onClick={() => { actions.signApproval(p.id, approval.id, name.trim(), sig ?? undefined); onClose(); }}><Icon.check width={15} />Sign approval</button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------ Your spaces */
/* A room-by-room way in for a client who doesn't read drawings: each space from
   the agreed schedule, with the sheets and images that show it. */
function ClientSpacesTab({ p }: { p: any }) {
  const spaces: any[] = p.brief?.spaces ?? [];
  const [openKey, setOpenKey] = useState<string | null>(spaces[0]?.name ?? null);
  const drawn = (p.deliverables ?? []).filter((d: any) => (d.previewUrl || d.fileUrl) && !d.archived);
  const designs = (p.presentation?.spaces ?? []);
  const unit = p.brief?.areaUnit === "sqm" ? "m²" : "ft²";

  /** Sheets whose title mentions this space, plus the general plans. */
  function sheetsFor(name: string) {
    const words = name.toLowerCase().split(/\s+/).filter((w: string) => w.length > 3);
    const direct = drawn.filter((d: any) => words.some((w: string) => d.title.toLowerCase().includes(w)));
    const plans = drawn.filter((d: any) => /plan/i.test(d.title) && !direct.includes(d));
    return { direct, plans: plans.slice(0, 4) };
  }
  function designFor(name: string) {
    const words = name.toLowerCase().split(/\s+/).filter((w: string) => w.length > 3);
    return designs.find((s: any) => words.some((w: string) => (s.name ?? "").toLowerCase().includes(w)));
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Your spaces</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 600 }}>
        Room by room, the way you'll actually use the building — what each space is, how big it is, and the drawings and images that show it. No drawing-reading required.
      </p>
      <div className="col" style={{ gap: 10 }}>
        {spaces.map((s: any) => {
          const open = openKey === s.name;
          const { direct, plans } = sheetsFor(s.name);
          const design = designFor(s.name);
          return (
            <div key={s.name} className="card reveal" style={{ overflow: "hidden" }}>
              <button className="row between" onClick={() => setOpenKey(open ? null : s.name)}
                style={{ width: "100%", padding: "14px 18px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left", gap: 10 }}>
                <span className="row" style={{ gap: 11, minWidth: 0, alignItems: "center" }}>
                  <Icon.caretDown width={15} style={{ transform: open ? "none" : "rotate(-90deg)", transition: "transform .18s", color: "var(--ink-3)", flexShrink: 0 }} />
                  <span style={{ fontSize: 16, fontWeight: 600 }}>{s.name}</span>
                  {s.qty > 1 && <span className="badge" style={{ fontSize: 9 }}>×{s.qty}</span>}
                </span>
                <span className="dim" style={{ fontSize: 12.5, flexShrink: 0 }}>{s.area ? `${s.area} ${unit}${s.qty > 1 ? " each" : ""}` : ""}{s.zone ? ` · ${s.zone}` : ""}</span>
              </button>
              {open && (
                <div style={{ padding: "0 18px 18px" }}>
                  {s.note && <div className="dim" style={{ fontSize: 12.5, marginBottom: 10, lineHeight: 1.6 }}>{s.note}</div>}
                  {design?.url && (
                    <div style={{ marginBottom: 12 }}>
                      <div className="kicker" style={{ fontSize: 8.5, marginBottom: 6 }}>How it's designed</div>
                      <img src={design.url} alt={s.name} style={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 10, border: "1px solid var(--line)" }} />
                    </div>
                  )}
                  {direct.length > 0 && (
                    <>
                      <div className="kicker" style={{ fontSize: 8.5, marginBottom: 6 }}>Drawings of this space</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10, marginBottom: 12 }}>
                        {direct.slice(0, 6).map((d: any) => (
                          <a key={d.id} href={d.fileUrl || d.previewUrl} target="_blank" rel="noreferrer" style={{ display: "block", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                            {d.previewUrl && <img src={d.previewUrl} alt={d.title} style={{ width: "100%", height: 92, objectFit: "cover", display: "block" }} />}
                            <div style={{ padding: "6px 8px" }}><div className="mono dim" style={{ fontSize: 10 }}>{d.code}</div><div style={{ fontSize: 11.5 }}>{d.title}</div></div>
                          </a>
                        ))}
                      </div>
                    </>
                  )}
                  {direct.length === 0 && plans.length > 0 && (
                    <>
                      <div className="kicker" style={{ fontSize: 8.5, marginBottom: 6 }}>Find it on the plans</div>
                      <div className="row wrap" style={{ gap: 6 }}>
                        {plans.map((d: any) => <a key={d.id} className="chip" style={{ fontSize: 11.5 }} href={d.fileUrl || d.previewUrl} target="_blank" rel="noreferrer">{d.code} · {d.title}</a>)}
                      </div>
                    </>
                  )}
                  {direct.length === 0 && plans.length === 0 && <div className="dim" style={{ fontSize: 12.5 }}>No drawings issued for this space yet.</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Designs */
/* The client's view of the issued construction drawings — the set revisions the
   studio has shared, by phase, in full quality. Read-only: the drawing IS the
   record, and it opens/downloads at full resolution. */
/* Packages the studio has shared with this client — open, download, and leave
   revision comments right in the portal (the emailed link posts the same way). */
function ClientPackagesTab({ p, who }: { p: any; who: string }) {
  const packages = ((p.packages ?? []) as any[]).filter((s) => s.audience === "client");
  const byId = new Map((p.deliverables ?? []).map((d: any) => [d.id, d]));
  return (
    <div style={{ maxWidth: 820 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Shared with you</h2>
      <p className="dim reveal" style={{ fontSize: 13.5, marginBottom: 22 }}>Drawing packages the studio has shared for your review. Open or download the sheets, and note any revisions you'd like.</p>
      <div className="col" style={{ gap: 22 }}>
        {packages.map((pkg) => {
          const sheets = (pkg.deliverableIds ?? []).map((id: string) => byId.get(id)).filter(Boolean) as any[];
          const byDisc = new Map<string, any[]>();
          for (const d of sheets) { const k = d.discipline || "Drawings"; if (!byDisc.has(k)) byDisc.set(k, []); byDisc.get(k)!.push(d); }
          return (
            <div key={pkg.id} className="card reveal" style={{ padding: 20 }}>
              <div className="row between wrap" style={{ gap: 8, marginBottom: 8 }}>
                <div><span className="mono dim" style={{ fontSize: 11 }}>{pkg.ref}</span> <span className="serif" style={{ fontSize: 20 }}>{pkg.title}</span></div>
                <div className="row" style={{ gap: 10, alignItems: "center" }}>
                  <span className="dim" style={{ fontSize: 12 }}>{sheets.length} sheet{sheets.length === 1 ? "" : "s"} · {fmtDate(pkg.sharedAt)}</span>
                  <button className="btn ghost sm" title="Download transmittal record (PDF)" onClick={() => void downloadTransmittalPdf(p, pkg)}><Icon.download width={12} />Transmittal</button>
                  {pkg.receipt
                    ? <span className="row" style={{ gap: 5, alignItems: "center", color: "var(--ok, #2e7d32)", fontSize: 12 }}><Icon.check width={13} />Receipt confirmed · {fmtDate(pkg.receipt.at)}</span>
                    : <button className="btn accent sm" title="Confirm you have received this package" onClick={() => actions.acknowledgePackage(p.id, pkg.id, who)}><Icon.check width={12} />Confirm receipt</button>}
                </div>
              </div>
              {pkg.note && <p className="muted" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 12 }}>{pkg.note}</p>}
              <div className="col" style={{ gap: 12 }}>
                {[...byDisc.entries()].map(([disc, ds]) => (
                  <div key={disc}>
                    <div className="kicker" style={{ fontSize: 9, color: "var(--accent-ink)", marginBottom: 6 }}>{disc}</div>
                    <div className="col" style={{ gap: 6 }}>
                      {ds.map((d) => {
                        const href = d.fileUrl || d.previewUrl;
                        return (
                          <div key={d.id} className="panel" style={{ padding: "9px 12px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                            <span className="mono" style={{ fontSize: 12, fontWeight: 600, width: 74, flexShrink: 0 }}>{d.code}</span>
                            <span style={{ flex: 1, fontSize: 13, minWidth: 140 }}>{d.title}{d.revision ? <span className="dim mono" style={{ fontSize: 10.5, marginLeft: 6 }}>Rev {d.revision}</span> : null}</span>
                            {href ? (
                              <span className="row" style={{ gap: 6 }}>
                                <a className="btn ghost sm" target="_blank" rel="noreferrer" href={href}><Icon.arrowUpRight width={12} />Open</a>
                                <a className="btn accent sm" href={d.fileUrl || href} download={d.fileName || `${d.code}.pdf`}><Icon.download width={12} />PDF</a>
                              </span>
                            ) : <span className="dim" style={{ fontSize: 11 }}>No file</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <PackageComments p={p} pkg={pkg} who={who} />
            </div>
          );
        })}
        {packages.length === 0 && <p className="dim">Nothing shared yet — packages will appear here when the studio sends one.</p>}
      </div>
    </div>
  );
}

function PackageComments({ p, pkg, who }: { p: any; pkg: any; who: string }) {
  const [text, setText] = useState("");
  const [code, setCode] = useState("");
  const notes = (pkg.clientNotes ?? []) as any[];
  function send() {
    if (!text.trim()) return;
    actions.addPackageNote(p.id, pkg.id, { text: text.trim(), code: code.trim() || undefined, by: who });
    setText(""); setCode("");
  }
  return (
    <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
      <div className="kicker" style={{ fontSize: 9, marginBottom: 8 }}>Your review comments</div>
      <div className="col" style={{ gap: 6, marginBottom: 10 }}>
        {notes.map((n) => (
          <div key={n.id} className="row" style={{ gap: 8, fontSize: 12.5, opacity: n.resolved ? 0.6 : 1 }}>
            {n.code && <span className="badge mono" style={{ fontSize: 9 }}>{n.code}</span>}
            <span>{n.text}</span>
            <span className="dim" style={{ fontSize: 10.5, marginLeft: "auto", whiteSpace: "nowrap" }}>{n.by}{n.resolved ? " · addressed" : ""}</span>
          </div>
        ))}
        {notes.length === 0 && <p className="dim" style={{ fontSize: 12 }}>No comments yet.</p>}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <input className="input" style={{ width: 120 }} placeholder="Sheet (opt.)" value={code} onChange={(e) => setCode(e.target.value)} />
        <input className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Note a revision you'd like…" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} />
        <button className="btn accent sm" onClick={send} disabled={!text.trim()}><Icon.send width={13} />Send</button>
      </div>
    </div>
  );
}

function ClientDrawingsTab({ p }: { p: any }) {
  const [view, setView] = useState<{ reg: ViewerSheet[]; id: string } | null>(null);
  const [openSet, setOpenSet] = useState<string | null>(null);
  const [book, setBook] = useState(false);
  const phases = ["Conceptual", "Design Development", "IFT"];
  const phaseLabel: Record<string, string> = { "Conceptual": "Conceptual", "Design Development": "Design Development", "IFT": "Issued for Construction" };
  const toSheet = (x: any): ViewerSheet => ({ id: x.id ?? x.code, code: x.code, title: x.title, discipline: x.discipline, phase: x.phase, revision: x.revision, building: x.building, previewUrl: x.previewUrl, fileUrl: x.fileUrl, fileName: x.fileName, page: x.page, issues: x.issues, clouds: x.clouds });

  // the live register the client can see = sheets with a drawing on file, not archived
  const drawn = (p.deliverables ?? []).filter((d: any) => (d.previewUrl || d.fileUrl) && !d.archived);
  const blds = [...new Set(drawn.map((d: any) => d.building).filter(Boolean))] as string[];
  const rawGroups: [string, any[]][] = blds.length
    ? [...blds.map((b) => [b, drawn.filter((d: any) => d.building === b)] as [string, any[]]), ...(drawn.some((d: any) => !d.building) ? [["", drawn.filter((d: any) => !d.building)] as [string, any[]]] : [])]
    : [["", drawn]];
  const groups = rawGroups.filter(([, l]) => l.length > 0);
  const sets = ((p.drawingSets ?? []) as any[]).filter((s) => s.sharedWithClient);
  const withFiles = drawn.filter((d: any) => d.fileUrl);
  /* Downloads go through the splitter: a combined-set upload comes back as one
     PDF PER SHEET, named by drawing number — never the whole package again. */
  const [dl, setDl] = useState<string | null>(null);
  async function downloadAs(label: string, list: any[]) {
    if (dl) return;
    try {
      await downloadSheetFiles(
        list.map((d: any) => ({ code: d.code, title: d.title, revision: d.revision, fileUrl: d.fileUrl, fileName: d.fileName, page: d.page })),
        (done, total) => setDl(`${label} — ${done}/${total}`));
    } finally { setDl(null); }
  }
  // warm full-quality renders so opening a sheet is instant
  useEffect(() => { const s = drawn.filter((d: any) => d.fileUrl); if (s.length) prefetchSheets(s); }, [p.id, drawn.length]);

  /* The stages, as a client understands them — schematic first, construction
     last, each a clearly-titled chapter with its own download. */
  const STAGE_META: Record<string, { label: string; blurb: string }> = {
    "Conceptual": { label: "Schematic Design", blurb: "The scheme taking shape — plans, forms and the big decisions. These sheets evolve." },
    "Design Development": { label: "Design Development", blurb: "The design being resolved — dimensions, materials and systems firming up." },
    "IFT": { label: "Construction Drawings", blurb: "Issued for construction — the sheets the work is built and priced from." },
  };

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="row between wrap reveal" style={{ marginBottom: 6, gap: 10 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>Drawings</h2>
        <div className="row wrap" style={{ gap: 8 }}>
          {drawn.filter((d: any) => d.previewUrl).length > 1 && (
            <button className="btn accent sm" onClick={() => setBook(true)}><Icon.brief width={14} />Read the set as a book</button>
          )}
          {withFiles.length > 0 && (
            <button className="btn ghost sm" disabled={!!dl} onClick={() => void downloadAs("Preparing your set", withFiles)}>
              <Icon.download width={14} />{dl ?? (withFiles.length === 1 ? "Download the sheet" : `Download all ${withFiles.length} sheets`)}
            </button>
          )}
        </div>
      </div>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 620 }}>
        The set, stage by stage — from schematic design through to construction drawings. Open any sheet full-screen, <b>highlight the exact element</b> you want to talk about and leave a note on it, or download a stage as a package.
      </p>

      {drawn.length === 0 && <div className="card pad dim reveal" style={{ fontSize: 14 }}>No drawings have been shared yet — they'll appear here as your studio issues them.</div>}

      {/* live register — STAGE first, buildings within */}
      {phases.map((ph) => {
        const inPhase = drawn.filter((x: any) => x.phase === ph);
        if (!inPhase.length) return null;
        const meta = STAGE_META[ph] ?? { label: phaseLabel[ph], blurb: "" };
        const stageBlds = blds.length ? groups.map(([b, l]) => [b, l.filter((x: any) => x.phase === ph)] as [string, any[]]).filter(([, l]) => l.length) : ([["", inPhase]] as [string, any[]][]);
        return (
          <div key={ph} className="reveal" style={{ marginBottom: 34 }}>
            <div className="row between wrap" style={{ gap: 10, alignItems: "flex-end", paddingBottom: 8, borderBottom: "2px solid var(--ink)", marginBottom: 4 }}>
              <div style={{ minWidth: 0 }}>
                <div className="kicker" style={{ fontSize: 9 }}>Stage</div>
                <h3 className="serif" style={{ fontSize: 24, marginTop: 2 }}>{meta.label}</h3>
              </div>
              <div className="row" style={{ gap: 8, alignItems: "center", flexShrink: 0 }}>
                <span className="dim" style={{ fontSize: 12 }}>{inPhase.length} sheet{inPhase.length === 1 ? "" : "s"}</span>
                {inPhase.some((x: any) => x.fileUrl) && (
                  <button className="btn soft sm" disabled={!!dl} onClick={() => void downloadAs(`Preparing ${meta.label}`, inPhase.filter((x: any) => x.fileUrl))}>
                    <Icon.download width={13} />{dl ?? `Download this stage (${inPhase.filter((x: any) => x.fileUrl).length})`}
                  </button>
                )}
              </div>
            </div>
            {meta.blurb && <p className="dim" style={{ fontSize: 12.5, margin: "6px 0 14px", maxWidth: 560 }}>{meta.blurb}</p>}
            {stageBlds.map(([bld, list]) => (
              <div key={bld || "_"} style={{ marginBottom: 14 }}>
                {blds.length > 0 && <div className="kicker" style={{ fontSize: 9, margin: "8px 0" }}>{bld || "General"} · {list.length}</div>}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12 }}>
                  {list.map((x: any) => (
                    <button key={x.id ?? x.code} className="panel" onClick={() => setView({ reg: drawn.map(toSheet), id: x.id ?? x.code })} style={{ padding: 0, overflow: "hidden", textAlign: "left", cursor: "pointer", border: "1px solid var(--line)", background: "var(--card)" }}>
                      <div style={{ aspectRatio: "1.35", background: "var(--paper-2)", overflow: "hidden" }}>
                        {x.previewUrl ? <img src={x.previewUrl} alt={x.code} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : <span className="dim" style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 11 }}>{x.code}</span>}
                      </div>
                      <div style={{ padding: "7px 9px" }}>
                        <div className="mono" style={{ fontSize: 10 }}>{x.code} · {x.revision}</div>
                        <div style={{ fontSize: 10.5, lineHeight: 1.3, marginTop: 2 }}>{x.title}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* formally issued packages, as a record */}
      {sets.length > 0 && (
        <div className="reveal" style={{ marginTop: 8 }}>
          <h3 className="serif" style={{ fontSize: 20, marginBottom: 12 }}>Issued sets</h3>
          <div className="col" style={{ gap: 10 }}>
            {sets.map((s) => {
              const on = openSet === s.id;
              return (
                <div key={s.id} className="card" style={{ overflow: "hidden" }}>
                  <button className="row between" onClick={() => setOpenSet(on ? null : s.id)} style={{ width: "100%", padding: "13px 18px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
                    <span><span style={{ fontWeight: 600, fontSize: 15 }}>{s.label}</span><span className="dim" style={{ fontSize: 12, display: "block", marginTop: 2 }}>{s.sheets.length} sheets · issued {fmtDate(s.issuedOn)}{s.note ? ` · ${s.note}` : ""}</span></span>
                    <Icon.caretDown width={16} style={{ transform: on ? "none" : "rotate(-90deg)", transition: "transform .2s", color: "var(--ink-3)" }} />
                  </button>
                  {on && (
                    <div style={{ padding: "0 18px 16px", display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 10 }}>
                      {s.sheets.map((x: any, i: number) => (
                        <button key={i} className="panel" onClick={() => setView({ reg: s.sheets.map(toSheet), id: x.id ?? x.code })} style={{ padding: 0, overflow: "hidden", textAlign: "left", cursor: "pointer", border: "1px solid var(--line)", background: "var(--card)" }}>
                          <div style={{ aspectRatio: "1.35", background: "var(--paper-2)", overflow: "hidden" }}>
                            {x.previewUrl ? <img src={x.previewUrl} alt={x.code} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : <span className="dim" style={{ display: "grid", placeItems: "center", height: "100%", fontSize: 11 }}>{x.code}</span>}
                          </div>
                          <div style={{ padding: "6px 9px" }}><div className="mono" style={{ fontSize: 10 }}>{x.code} · {x.revision}</div><div style={{ fontSize: 10.5, lineHeight: 1.3, marginTop: 2 }}>{x.title}</div></div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {book && (
        <BookViewer
          title={`${p.name} — the drawing set`}
          sheets={phases.flatMap((ph) => drawn.filter((x: any) => x.phase === ph)).map((x: any) => ({ id: x.id ?? x.code, code: x.code, title: x.title, discipline: x.discipline, phase: STAGE_META[x.phase]?.label ?? x.phase, revision: x.revision, previewUrl: x.previewUrl, fileUrl: x.fileUrl }))}
          onInspect={(s) => { setBook(false); setView({ reg: drawn.map(toSheet), id: s.id }); }}
          onClose={() => setBook(false)}
        />
      )}

      {view && <DrawingViewer sheet={view.reg.find((s) => s.id === view.id)!} registry={view.reg} onNavigate={(s) => setView((v) => v && { ...v, id: s.id })} onClose={() => setView(null)}
        notesApi={{
          sheetKey: sheetKeyOf, notes: p.drawingNotes ?? [], by: p.client.name, author: "client",
          onAdd: (n) => actions.addDrawingNote(p.id, { ...n, author: "client", by: p.client.name }),
          onReply: (id, body) => actions.replyDrawingNote(p.id, id, { author: "client", by: p.client.name, body }),
        }} />}
    </div>
  );
}

function DesignsTab({ p }: { p: any }) {
  const [modal, setModal] = useState<DesignSpace | null>(null);
  const [comment, setComment] = useState("");
  function respond(space: DesignSpace, response: "approved" | "revise") {
    actions.respondToSpace(p.id, space.id, response, comment);
    setModal(null); setComment("");
  }
  return (
    <div>
      <div className="row between reveal" style={{ marginBottom: 6 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>{p.presentation.title}</h2>
        <StatusBadge status={p.presentation.status} />
      </div>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 560 }}>Take your time with each space. Approve what you love, or ask for a revision with a note — we'll take it from there.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px,1fr))", gap: 20 }}>
        {p.presentation.spaces.map((s: DesignSpace, i: number) => (
          <div key={s.id} className="card hoverable reveal" style={{ overflow: "hidden" }}>
            <div style={{ position: "relative" }}>
              <Plate hue={s.hue} h={180} seed={i + 1} label={s.option} />
              <div style={{ position: "absolute", top: 12, right: 12 }}><StatusBadge status={s.response} /></div>
            </div>
            <div style={{ padding: 20 }}>
              <h3 className="serif" style={{ fontSize: 22 }}>{s.name}</h3>
              <p className="muted" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.55 }}>{s.description}</p>
              <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>{s.materials.map((m) => <span key={m} className="badge">{m}</span>)}</div>
              {s.comment && <div className="panel" style={{ padding: "10px 12px", marginTop: 12, fontSize: 13.5, fontStyle: "italic" }}>“{s.comment}”</div>}
              <div className="row" style={{ gap: 8, marginTop: 16 }}>
                {s.response === "approved"
                  ? <span className="badge ok" style={{ padding: "8px 14px" }}><Icon.check width={14} />Approved</span>
                  : <>
                      <button className="btn accent sm" onClick={() => { setComment(""); actions.respondToSpace(p.id, s.id, "approved", ""); }}><Icon.check width={14} />Approve</button>
                      <button className="btn ghost sm" onClick={() => { setComment(s.comment ?? ""); setModal(s); }}><Icon.chat width={14} />Comment</button>
                    </>}
              </div>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal ? `Comment on ${modal.name}` : ""}>
        {modal && <div className="col" style={{ gap: 14 }}>
          <p className="muted" style={{ fontSize: 14 }}>Tell us what you'd like to see adjusted on <b>{modal.option}</b>.</p>
          <textarea className="textarea" autoFocus placeholder="e.g. Prefer a warmer metal — brass instead of steel…" value={comment} onChange={(e) => setComment(e.target.value)} />
          <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
            <button className="btn accent" onClick={() => respond(modal, "approved")}><Icon.check width={15} />Approve with note</button>
            <button className="btn" onClick={() => comment.trim() && respond(modal, "revise")}><Icon.send width={15} />Request revision</button>
          </div>
        </div>}
      </Modal>
    </div>
  );
}

/* ---------------------------------------------------------------- Progress */
/** A one-line hint of what's inside a stage, so the card is worth opening. */
function stageSummary(p: any, key: string): string {
  const taskPhase: Record<string, string> = { engagement: "Engagement", concept: "Concept", developed: "Developed", presentation: "Presentation", documentation: "Documentation", construction: "Construction" };
  const drawPhase: Record<string, string> = { concept: "Conceptual", developed: "Design Development", documentation: "IFT" };
  const tasks = (p.program ?? []).filter((t: any) => t.phase === taskPhase[key]);
  const draws = (p.deliverables ?? []).filter((d: any) => d.phase === drawPhase[key]);
  const bits: string[] = [];
  if (tasks.length) bits.push(`${tasks.filter((t: any) => t.status === "done").length}/${tasks.length} tasks`);
  if (draws.length) bits.push(`${draws.length} drawings`);
  return bits.join(" · ");
}

/** The key dates the studio is working to. Real calendar events once the studio
 *  has planned them (they flow in from the mind map); a provisional programme
 *  from the approved brief until then, clearly flagged as not-yet-fixed. */
function KeyDates({ p }: { p: any }) {
  const events: any[] = (p.schedule ?? [])
    .filter((e: any) => ["milestone", "meeting", "deadline", "site"].includes(e.kind))
    .slice().sort((a: any, b: any) => +new Date(a.date) - +new Date(b.date));
  const provisional = !events.length && milestonesReady(p)
    ? proposedMilestones(p, todayISO()).map((m) => ({ id: m.key, title: m.title, date: m.date, kind: m.kind, note: m.note, stage: m.stage }))
    : [];
  const rows = events.length ? events : provisional;
  if (!rows.length) return null;
  const isProvisional = !events.length;
  const nextI = rows.findIndex((r: any) => daysUntil(r.date) >= 0);
  return (
    <div className="card pad reveal" style={{ marginBottom: 24 }}>
      <div className="row between wrap" style={{ gap: 10, marginBottom: 14 }}>
        <div>
          <div className="kicker">Key dates &amp; milestones</div>
          <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>
            {isProvisional ? "Provisional — the studio confirms these as the design develops" : "The dates the studio is working to"}
          </div>
        </div>
        {nextI >= 0 && <span className="badge accent"><span className="dot" />Next · {relative(rows[nextI].date)}</span>}
      </div>
      <div className="col" style={{ gap: 0 }}>
        {rows.map((e: any, i: number) => {
          const past = daysUntil(e.date) < 0;
          const isNext = i === nextI;
          const stageDone = e.stage && (p.stages ?? []).find((s: any) => s.key === e.stage)?.status === "done";
          return (
            <div key={e.id} className="row between" style={{ padding: "11px 0", borderTop: i ? "1px solid var(--line)" : "none", gap: 12 }}>
              <span className="row" style={{ gap: 13, minWidth: 0 }}>
                <span style={{ width: 32, textAlign: "center", flexShrink: 0, opacity: past && !stageDone ? 0.6 : 1 }}>
                  <div className="serif" style={{ fontSize: 18, lineHeight: 1, color: isNext ? "var(--accent-ink)" : "var(--ink)" }}>{fmtDate(e.date, { day: "numeric" })}</div>
                  <div className="dim" style={{ fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em" }}>{fmtDate(e.date, { month: "short" })}</div>
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{e.title}</span>
                  {e.note && <span className="dim" style={{ fontSize: 12, display: "block", lineHeight: 1.4 }}>{e.note}</span>}
                </span>
              </span>
              <span className="row" style={{ gap: 8, flexShrink: 0 }}>
                {stageDone ? <span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />done</span>
                  : isNext ? <span className="badge accent" style={{ fontSize: 9 }}>{relative(e.date)}</span>
                  : <span className="dim" style={{ fontSize: 11 }}>{past ? "passed" : relative(e.date)}</span>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProgressTab({ p }: { p: any }) {
  const [openStage, setOpenStage] = useState<number | null>(null);
  const program: any[] = p.program ?? [];
  const done = program.filter((t) => t.status === "done");
  const live = program.filter((t) => t.status !== "done");
  const pct = program.length ? Math.round((done.length / program.length) * 100) : 0;
  const notices: any[] = p.notices ?? [];
  const siteLog: any[] = [...(p.siteLog ?? [])].sort((a, b) => +new Date(b.on) - +new Date(a.on));
  const latestPct = siteLog.find((u) => typeof u.percent === "number")?.percent;
  return (
    <div style={{ maxWidth: 720 }}>
      {/* the journey, drawn — every recorded moment in order */}
      <ClientTimeline p={p} />
      {/* site progress — construction-monitoring updates from the studio */}
      {siteLog.length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden", marginBottom: 24 }}>
          <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">Site progress</span>
            {typeof latestPct === "number" && <span className="badge accent">{latestPct}% complete</span>}
          </div>
          <div className="col" style={{ gap: 0 }}>
            {siteLog.slice(0, 8).map((u, i) => (
              <div key={u.id} style={{ padding: "14px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <div className="row between wrap" style={{ gap: 8 }}><span style={{ fontWeight: 600, fontSize: 14 }}>{u.title}</span><span className="dim" style={{ fontSize: 11.5 }}>{[u.zone, relative(u.on)].filter(Boolean).join(" · ")}</span></div>
                {typeof u.percent === "number" && <div style={{ margin: "8px 0" }}><Meter value={u.percent} /></div>}
                {u.note && <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.55 }}>{u.note}</div>}
                {u.photos?.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(120px,1fr))", gap: 6, marginTop: 8 }}>{u.photos.map((ph: string, j: number) => <a key={j} href={ph} target="_blank" rel="noreferrer"><img src={ph} alt="" style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 6, border: "1px solid var(--line)" }} /></a>)}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* the programme, drawn honestly — where each phase sits against today */}
      {program.length > 0 && (() => {
        const dated = program.filter((t: any) => t.start && t.end);
        if (!dated.length) return null;
        const t0 = Math.min(...dated.map((t: any) => +new Date(t.start)));
        const t1 = Math.max(...dated.map((t: any) => +new Date(t.end)));
        const span = Math.max(1, t1 - t0);
        const pos = (d: string) => ((+new Date(d) - t0) / span) * 100;
        const nowPct = Math.min(100, Math.max(0, ((Date.now() - t0) / span) * 100));
        // group by phase so the client sees phases, not 60 task rows
        const byPhase = new Map<string, { start: number; end: number; done: number; total: number }>();
        for (const t of dated) {
          const k = t.phase || "Programme";
          const g = byPhase.get(k) ?? { start: +new Date(t.start), end: +new Date(t.end), done: 0, total: 0 };
          g.start = Math.min(g.start, +new Date(t.start)); g.end = Math.max(g.end, +new Date(t.end));
          g.total++; if (t.status === "done") g.done++;
          byPhase.set(k, g);
        }
        const iso = (n: number) => new Date(n).toISOString().slice(0, 10);
        return (
          <div className="card reveal" style={{ overflow: "hidden", marginBottom: 24 }}>
            <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker">Programme</span>
              <span className="dim" style={{ fontSize: 12 }}>{fmtDate(iso(t0))} — {fmtDate(iso(t1))}</span>
            </div>
            <div style={{ padding: "16px 18px", position: "relative" }}>
              {/* today */}
              {nowPct > 0 && nowPct < 100 && (
                <div style={{ position: "absolute", left: `calc(18px + ${nowPct}% - ${nowPct * 0.36}px)`, top: 10, bottom: 10, width: 1.5, background: "var(--accent)", opacity: .55, zIndex: 1 }} title="Today" />
              )}
              <div className="col" style={{ gap: 12 }}>
                {[...byPhase.entries()].sort((a, b) => a[1].start - b[1].start).map(([phase, g]) => {
                  const left = pos(iso(g.start)), width = Math.max(3, pos(iso(g.end)) - pos(iso(g.start)));
                  const pct = g.total ? Math.round((g.done / g.total) * 100) : 0;
                  return (
                    <div key={phase}>
                      <div className="row between" style={{ fontSize: 12.5, marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{phase}</span>
                        <span className="dim" style={{ fontSize: 11.5 }}>{fmtDate(iso(g.start))} – {fmtDate(iso(g.end))} · {pct}% done</span>
                      </div>
                      <div style={{ position: "relative", height: 16, background: "var(--paper-2)", borderRadius: 8 }}>
                        <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: 0, bottom: 0, borderRadius: 8, background: "var(--line-2)", overflow: "hidden" }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: pct === 100 ? "var(--ok)" : "var(--accent)", opacity: pct === 100 ? .5 : .75 }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.5 }}>Each bar is a phase, shaded by how much of it is complete. The vertical line is today. Dates move as the work moves — we keep this honest rather than optimistic.</div>
            </div>
          </div>
        );
      })()}

      {/* snag list — what's outstanding at handover */}
      {((p.snags ?? []).length > 0) && (() => {
        const snags: any[] = p.snags ?? [];
        const open = snags.filter((s) => s.status !== "accepted").length;
        return (
          <div className="card reveal" style={{ overflow: "hidden", marginBottom: 24 }}>
            <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker">Snag list</span>
              <span className="dim" style={{ fontSize: 12 }}>{snags.length} item{snags.length === 1 ? "" : "s"} · {open} outstanding</span>
            </div>
            <div className="col" style={{ gap: 0 }}>
              {snags.slice(0, 12).map((s, i) => (
                <div key={s.id} className="row between wrap" style={{ padding: "10px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                  <span className="row" style={{ gap: 9, minWidth: 0 }}>
                    <span className="mono dim" style={{ fontSize: 11 }}>{s.ref}</span>
                    <span style={{ fontSize: 13.5 }}>{s.title}</span>
                    {s.location && <span className="dim" style={{ fontSize: 11.5 }}>· {s.location}</span>}
                  </span>
                  <span className="badge" style={{ fontSize: 9, color: s.status === "accepted" ? "var(--ok)" : undefined }}>{s.status === "accepted" ? "Accepted" : s.status === "fixed" ? "Fixed" : "Open"}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      {/* what's happened lately — raised when work is ticked off on the mind map */}
      {notices.length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden", marginBottom: 24 }}>
          <div className="row between" style={{ padding: "13px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">Recent updates</span>
            <span className="dim" style={{ fontSize: 12 }}>{notices.length}</span>
          </div>
          {notices.slice(0, 8).map((n, i) => (
            <div key={n.id} className="row between" style={{ padding: "11px 18px", borderTop: i ? "1px solid var(--line)" : "none", gap: 10, background: n.seen ? "transparent" : "color-mix(in srgb, var(--ok) 5%, var(--card))" }}>
              <span className="row" style={{ gap: 10, minWidth: 0 }}>
                <Icon.checkCircle width={15} style={{ color: "var(--ok)", flexShrink: 0 }} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 500 }}>{n.title}</span>
                  {n.body && <span className="dim" style={{ fontSize: 12, display: "block" }}>{n.body}</span>}
                </span>
              </span>
              <span className="dim" style={{ fontSize: 11, flexShrink: 0 }}>{relative(n.on)}</span>
            </div>
          ))}
        </div>
      )}

      {/* the milestone spine — the dates the studio is working to */}
      <KeyDates p={p} />

      {/* the work programme, mirrored from the studio's mind map */}
      {program.length > 0 && (
        <div className="card pad reveal" style={{ marginBottom: 24 }}>
          <div className="row between wrap" style={{ gap: 10, marginBottom: 10 }}>
            <div>
              <div className="kicker">Design work programme</div>
              <div className="dim" style={{ fontSize: 12.5, marginTop: 3 }}>{done.length} of {program.length} tasks complete</div>
            </div>
            <span className="serif" style={{ fontSize: 26 }}>{pct}%</span>
          </div>
          <Meter value={pct} />
          <div className="col" style={{ gap: 0, marginTop: 14 }}>
            {[...done, ...live].map((t, i) => (
              <div key={t.id} className="row between" style={{ padding: "9px 0", borderTop: i ? "1px solid var(--line)" : "none", gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0 }}>
                  <span style={{ width: 18, height: 18, borderRadius: 18, flexShrink: 0, display: "grid", placeItems: "center", background: t.status === "done" ? "var(--ok)" : "transparent", border: "1.5px solid " + (t.status === "done" ? "var(--ok)" : t.status === "blocked" ? "var(--warn)" : "var(--line-2)"), color: "#fff" }}>
                    {t.status === "done" && <Icon.check width={11} />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, textDecoration: t.status === "done" ? "line-through" : "none", color: t.status === "done" ? "var(--ink-3)" : "var(--ink)" }}>{t.name}</span>
                    {(t.start || t.end) && <span className="dim" style={{ fontSize: 11, display: "block" }}>{fmtDay(t.start)} – {fmtDay(t.end)}</span>}
                  </span>
                </span>
                <span className="row" style={{ gap: 8, flexShrink: 0 }}>
                  <span className="dim" style={{ fontSize: 11 }}>{t.phase}</span>
                  {t.status === "doing" && <span className="badge accent" style={{ fontSize: 9 }}>{t.progress}%</span>}
                  {t.status === "blocked" && <span className="badge warn" style={{ fontSize: 9 }}>held</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 20 }}>Where we are</h2>
      {p.stages.map((s: any, i: number) => {
        const active = s.status === "active", done = s.status === "done";
        return (
          <div key={s.key} className="reveal" style={{ display: "grid", gridTemplateColumns: "36px 1fr", gap: 16, paddingBottom: i === p.stages.length - 1 ? 0 : 20, position: "relative" }}>
            {i < p.stages.length - 1 && <div style={{ position: "absolute", left: 17, top: 32, bottom: 0, width: 2, background: done ? "var(--accent)" : "var(--line-2)" }} />}
            <div style={{ width: 36, height: 36, borderRadius: 36, display: "grid", placeItems: "center", zIndex: 1, background: done ? "var(--accent)" : active ? "var(--ink)" : "var(--card)", color: done || active ? "#fff" : "var(--ink-3)", border: "1px solid " + (active ? "var(--accent)" : "var(--line-2)"), boxShadow: active ? "0 0 0 5px var(--accent-soft)" : "none" }}>{done ? <Icon.check width={16} /> : <span className="mono" style={{ fontSize: 12 }}>{i + 1}</span>}</div>
            <button className="card pad stage-row" onClick={() => setOpenStage(i)}
              style={{ textAlign: "left", cursor: "pointer", width: "100%", display: "block", background: active ? "color-mix(in srgb,var(--accent) 5%,var(--card))" : "var(--card)", borderColor: active ? "color-mix(in srgb,var(--accent) 35%,var(--line))" : "var(--line)" }}>
              <div className="row between wrap" style={{ gap: 8 }}>
                <h3 className="serif" style={{ fontSize: 19 }}>{s.label}</h3>
                <span className="row wrap" style={{ gap: 6 }}>
                  {active && <span className="badge accent"><span className="dot" />You are here</span>}
                  <span className={TONE_CLASS[stageState(p, s, "client").tone]} style={{ fontSize: 9.5 }}>{stageState(p, s, "client").label}</span>
                </span>
              </div>
              <div className="muted" style={{ marginTop: 6, fontSize: 14 }}>{s.blurb}</div>
              <div className="row between" style={{ marginTop: 10, gap: 10 }}>
                <span className="dim" style={{ fontSize: 11.5 }}>{stageSummary(p, s.key)}</span>
                <span className="stage-cta row" style={{ gap: 5, fontSize: 11.5, color: "var(--accent-ink)", fontWeight: 600 }}>What happens here<Icon.arrowUpRight width={12} /></span>
              </div>
            </button>
          </div>
        );
      })}

      <StageLightbox stage={openStage === null ? null : p.stages[openStage]} index={openStage ?? 0} p={p} onClose={() => setOpenStage(null)} />
      <style>{`
        .stage-row { transition: transform .28s var(--ease-out), box-shadow .28s var(--ease-out), border-color .2s ease; }
        .stage-row:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .stage-row:active { transform: translateY(0) scale(.996); }
        .stage-row .stage-cta { opacity: .5; transition: opacity .25s ease, gap .25s ease; }
        .stage-row:hover .stage-cta { opacity: 1; gap: 9px; }
        @media (prefers-reduced-motion: reduce){ .stage-row, .stage-row .stage-cta { transition: none } .stage-row:hover { transform: none } }
      `}</style>
    </div>
  );
}

/* ---------------------------------------------------------------- Direction */
/** The concept directions, once the studio has presented them — the client says
    which one they're drawn to, and why. One preference at a time. */
function DirectionTab({ p }: { p: any }) {
  const [open, setOpen] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const concepts: any[] = p.concepts ?? [];
  const picked = concepts.find((c) => c.response?.choice === "preferred");

  function respond(cid: string, choice: "preferred" | "no") {
    actions.respondToConcept(p.id, cid, choice, comment, p.client.name);
    setOpen(null); setComment("");
  }

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 6 }}>Concept directions</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 620 }}>
        Two or three ways {p.name} could go. Have a look through and tell us which one you're drawn to — you can change your mind later, this is the start of the conversation.
      </p>
      {picked && (
        <div className="card pad reveal" style={{ marginBottom: 20, borderColor: "color-mix(in srgb,var(--accent) 35%,var(--line))", background: "color-mix(in srgb,var(--accent) 5%,var(--card))" }}>
          <span className="row" style={{ gap: 8 }}><Icon.check width={15} style={{ color: "var(--accent-ink)" }} /><span style={{ fontSize: 14 }}>You're drawn to <b>{picked.title}</b> — we're taking that forward.</span></span>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 20 }}>
        {concepts.map((c, i) => {
          const pref = c.response?.choice === "preferred";
          const out = c.response?.choice === "no";
          return (
            <div key={c.id} className="card reveal" style={{ overflow: "hidden", display: "flex", flexDirection: "column", opacity: out ? 0.6 : 1, borderColor: pref ? "var(--accent)" : "var(--line)" }}>
              {c.url
                ? <img src={c.url} alt={c.title} style={{ width: "100%", height: 190, objectFit: "cover", display: "block" }} />
                : <Plate hue={c.hue} h={190} seed={i + 1} label={`0${i + 1}`} />}
              <div style={{ padding: 18, flex: 1, display: "flex", flexDirection: "column" }}>
                <div className="row between wrap" style={{ gap: 8 }}>
                  <h3 className="serif" style={{ fontSize: 20 }}>{c.title}</h3>
                  {pref && <span className="badge accent" style={{ fontSize: 9 }}><Icon.check width={9} />Your pick</span>}
                  {out && <span className="badge" style={{ fontSize: 9 }}>Not this one</span>}
                </div>
                {c.note && <p className="muted" style={{ marginTop: 8, fontSize: 13.5, lineHeight: 1.55 }}>{c.note}</p>}
                {(c.tags ?? []).length > 0 && (
                  <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>{c.tags.map((t: string) => <span key={t} className="badge" style={{ fontSize: 9.5 }}>{t}</span>)}</div>
                )}
                {c.response?.comment && (
                  <div className="panel" style={{ padding: "8px 11px", marginTop: 12, fontSize: 12.5 }}>{c.response.comment}</div>
                )}
                {open === c.id ? (
                  <div className="col" style={{ gap: 8, marginTop: 14 }}>
                    <textarea className="input" rows={3} autoFocus value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What draws you to it — or what doesn't?" />
                    <div className="row wrap" style={{ gap: 8 }}>
                      <button className="btn accent sm" onClick={() => respond(c.id, "preferred")}><Icon.check width={13} />I'm drawn to this</button>
                      <button className="btn ghost sm" onClick={() => respond(c.id, "no")}>Not this one</button>
                      <button className="btn ghost sm" onClick={() => { setOpen(null); setComment(""); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn soft sm" style={{ marginTop: "auto", alignSelf: "flex-start", marginBlockStart: 14 }}
                    onClick={() => { setOpen(c.id); setComment(c.response?.comment ?? ""); }}>
                    {c.response ? "Change my response" : "Tell us what you think"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Consultants */
const CONSULTANT_SLOTS: { key: ConsultantDiscipline; label: string; blurb: string; icon: "stages" | "gear" }[] = [
  { key: "Structural", label: "Structural engineer", blurb: "Foundations, frame and stability — the engineer who designs and signs off the structure.", icon: "stages" },
  { key: "MEP", label: "MEP engineer", blurb: "Mechanical, electrical and plumbing — the services design and its coordination.", icon: "gear" },
];

function ConsultantsTab({ p }: { p: any }) {
  const list: Consultant[] = p.consultants ?? [];
  return (
    <div className="reveal" style={{ maxWidth: 760 }}>
      <h2 className="serif" style={{ fontSize: 30, marginBottom: 6 }}>Your consultants</h2>
      <p className="muted" style={{ marginBottom: 22, maxWidth: 630 }}>
        {STUDIO.name} leads the architecture and interiors. The structural and MEP engineers are appointed by you — enter your chosen firm here and we'll coordinate them into the design programme. You can change an appointment at any time.
      </p>
      <div className="col" style={{ gap: 16 }}>
        {CONSULTANT_SLOTS.map((d) => (
          <ConsultantSlot key={d.key} p={p} disc={d} current={list.find((c) => c.discipline === d.key)} />
        ))}
      </div>
    </div>
  );
}

function ConsultantSlot({ p, disc, current }: { p: any; disc: { key: ConsultantDiscipline; label: string; blurb: string; icon: "stages" | "gear" }; current?: Consultant }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ firm: "", contact: "", email: "", phone: "", note: "" });
  const Ico = Icon[disc.icon];

  function startEdit() {
    setF({ firm: current?.firm || "", contact: current?.contact || "", email: current?.email || "", phone: current?.phone || "", note: current?.note || "" });
    setEditing(true);
  }
  function appoint() {
    if (!f.firm.trim()) return;
    actions.appointConsultant(p.id, { discipline: disc.key, ...f });
    setEditing(false);
  }

  // appointed & not being edited — show the appointment with quick contact actions
  if (current && !editing) {
    return (
      <div className="card pad">
        <div className="row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 13, minWidth: 0 }}>
            <span style={{ width: 42, height: 42, borderRadius: 12, background: "var(--paper-2)", color: "var(--accent-ink)", display: "grid", placeItems: "center", flexShrink: 0 }}><Ico width={20} /></span>
            <div style={{ minWidth: 0 }}>
              <div className="row wrap" style={{ gap: 8 }}><span className="kicker" style={{ fontSize: 9 }}>{disc.label}</span><span className="badge ok" style={{ fontSize: 9 }}><Icon.check width={9} />Appointed</span></div>
              <div className="serif" style={{ fontSize: 20, marginTop: 3 }}>{current.firm}</div>
              {current.contact && <div className="dim" style={{ fontSize: 12.5 }}>{current.contact}{current.email ? ` · ${current.email}` : ""}</div>}
            </div>
          </div>
          <button className="btn ghost sm" onClick={startEdit}><Icon.swap width={13} />Change</button>
        </div>
        {current.note && <div className="panel dim" style={{ padding: "9px 12px", marginTop: 12, fontSize: 12.5, lineHeight: 1.5 }}>{current.note}</div>}
        <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
          {current.email && <a className="btn soft sm" href={`mailto:${current.email}?subject=${encodeURIComponent(p.name + " — " + disc.key)}`}><Icon.send width={13} />Email</a>}
          {current.phone && <button className="btn soft sm" style={{ background: "color-mix(in srgb,#25D366 14%,transparent)", borderColor: "color-mix(in srgb,#25D366 45%,transparent)", color: "#128C7E" }} onClick={() => openWhatsApp(`Hi ${current.contact || current.firm} — regarding ${p.name} (${p.type}). `, current.phone)}><Icon.chat width={13} />WhatsApp</button>}
          <button className="btn ghost sm" style={{ marginLeft: "auto", color: "var(--danger)", borderColor: "var(--danger)" }} onClick={() => actions.removeConsultant(p.id, current.id)}><Icon.close width={12} />Remove</button>
        </div>
      </div>
    );
  }

  // empty or editing — the appointment form
  return (
    <div className="card pad" style={{ borderStyle: editing ? "solid" : "dashed" }}>
      <div className="row between wrap" style={{ gap: 12 }}>
        <div className="row" style={{ gap: 13, minWidth: 0 }}>
          <span style={{ width: 42, height: 42, borderRadius: 12, background: "var(--paper-2)", color: "var(--ink-3)", display: "grid", placeItems: "center", flexShrink: 0 }}><Ico width={20} /></span>
          <div><div className="serif" style={{ fontSize: 19 }}>{disc.label}</div><div className="dim" style={{ fontSize: 12.5, marginTop: 2, maxWidth: 430 }}>{disc.blurb}</div></div>
        </div>
        {!editing && <button className="btn accent sm" onClick={startEdit}><Icon.plus width={14} />Appoint</button>}
      </div>
      {editing && (
        <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <CField label="Firm *" v={f.firm} on={(v) => setF({ ...f, firm: v })} ph="e.g. Meridian Structures" />
            <CField label="Contact" v={f.contact} on={(v) => setF({ ...f, contact: v })} ph="Named contact" />
            <CField label="Email" v={f.email} on={(v) => setF({ ...f, email: v })} ph="name@firm.com" />
            <CField label="Phone" v={f.phone} on={(v) => setF({ ...f, phone: v })} ph="+592 …" />
          </div>
          <CField label="Scope note" v={f.note} on={(v) => setF({ ...f, note: v })} ph="Anything the studio should know about their remit" />
          <div className="row wrap" style={{ gap: 8, marginTop: 2 }}>
            <button className="btn accent" disabled={!f.firm.trim()} onClick={appoint}><Icon.check width={15} />Appoint {disc.key}</button>
            <button className="btn ghost" onClick={() => setEditing(false)}>Cancel</button>
            <span className="dim" style={{ fontSize: 11.5, marginLeft: "auto", alignSelf: "center" }}>We'll let {STUDIO.name} know.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function CField({ label, v, on, ph }: { label: string; v: string; on: (v: string) => void; ph?: string }) {
  return (
    <label className="col" style={{ gap: 5 }}>
      <span className="kicker" style={{ fontSize: 9 }}>{label}</span>
      <input className="input" value={v} onChange={(e) => on(e.target.value)} placeholder={ph} />
    </label>
  );
}

/* ----------------------------------------------------------------- Reports */
function ReportsTab({ p }: { p: any }) {
  const issued = p.reports.filter((r: any) => r.status === "issued");
  return (
    <div style={{ maxWidth: 760 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 20 }}>Progress reports</h2>
      <div className="col" style={{ gap: 16 }}>
        {issued.map((r: any) => (
          <div key={r.id} className="card pad reveal">
            <div className="row between"><h3 className="serif" style={{ fontSize: 20 }}>{r.title}</h3><span className="dim" style={{ fontSize: 12.5 }}>{fmtDate(r.issuedOn)}</span></div>
            <p className="muted" style={{ marginTop: 10, lineHeight: 1.6 }}>{r.summary}</p>
            <div className="col" style={{ gap: 7, marginTop: 12 }}>{r.highlights.map((h: string, i: number) => <div key={i} className="row" style={{ gap: 10 }}><Icon.check width={15} style={{ color: "var(--ok)" }} /><span style={{ fontSize: 14 }}>{h}</span></div>)}</div>
          </div>
        ))}
        {issued.length === 0 && <div className="dim">No reports issued yet.</div>}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Billing */
/* The formal record of each completed design stage — provided in full: view it
   on the tokened page, download every sheet, or forward the link. */
function ClientSubmissionsTab({ p }: { p: any }) {
  const subs = p.submissions ?? [];
  const [copied, setCopied] = useState<string | null>(null);
  const link = (t?: string) => `${location.origin}/pkg/${t}`;
  return (
    <div style={{ maxWidth: 820 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 8 }}>Stage submissions</h2>
      <p className="muted reveal" style={{ marginBottom: 22, maxWidth: 620 }}>
        Each completed design stage, submitted in full — every sheet as it stood when the stage closed. Open a submission to view and download the complete package, or share the link with anyone who needs it.
      </p>
      <div className="col" style={{ gap: 14 }}>
        {subs.map((s: any) => (
          <div key={s.id} className="card pad reveal">
            <div className="row between wrap" style={{ gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div className="row wrap" style={{ gap: 8, alignItems: "baseline" }}>
                  <span className="badge accent mono" style={{ fontSize: 10.5 }}>{s.ref}</span>
                  <span className="serif" style={{ fontSize: 20 }}>{s.title}</span>
                </div>
                <div className="dim" style={{ fontSize: 12, marginTop: 4 }}>{s.sheets.length} sheets · submitted {fmtDate(s.submittedOn)}</div>
              </div>
              <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                <a className="btn accent sm" href={link(s.packageToken)} target="_blank" rel="noreferrer"
                  onClick={() => actions.markSubmissionViewed(p.id, s.id)}>
                  <Icon.arrowUpRight width={13} />View &amp; download
                </a>
                <button className="btn soft sm" onClick={() => { void navigator.clipboard?.writeText(link(s.packageToken)); setCopied(s.id); setTimeout(() => setCopied(null), 1600); }}>
                  {copied === s.id ? <><Icon.check width={13} />Copied</> : <><Icon.link width={13} />Share link</>}
                </button>
              </div>
            </div>
            {s.note && <p style={{ fontSize: 13.5, lineHeight: 1.6, marginTop: 10, maxWidth: 640 }}>{s.note}</p>}
            {/* what's inside, at a glance */}
            <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
              {[...new Set(s.sheets.map((x: any) => x.discipline))].map((d: any) => (
                <span key={d} className="badge" style={{ fontSize: 10 }}>{d} · {s.sheets.filter((x: any) => x.discipline === d).length}</span>
              ))}
            </div>
          </div>
        ))}
        {subs.length === 0 && <div className="dim reveal">No stage submissions yet — they arrive as each design stage completes.</div>}
      </div>
    </div>
  );
}

function BillingTab({ p }: { p: any }) {
  const owed = outstanding(p);
  const [proofFor, setProofFor] = useState<string | null>(null);
  const pendingVars = (p.variations ?? []).filter((v: any) => v.status === "issued");
  return (
    <div style={{ maxWidth: 780 }}>
      <div className="row between reveal" style={{ marginBottom: 20 }}>
        <h2 className="serif" style={{ fontSize: 30 }}>Billing</h2>
        <div style={{ textAlign: "right" }}><div className="kicker">Outstanding</div><div className="serif" style={{ fontSize: 26, color: owed ? "var(--accent-ink)" : "var(--ok)" }}>{money(owed, p.currency)}</div></div>
      </div>
      {/* payment certificates under the construction contract, mirrored from the
          Contract Studio — the certificate and the contractor's invoice together */}
      {(p.certificates ?? []).length > 0 && (
        <div className="card reveal" style={{ marginBottom: 16, overflow: "hidden" }}>
          <div className="row between wrap" style={{ padding: "12px 16px", borderBottom: "1px solid var(--line)", gap: 8 }}>
            <span className="kicker">Payment certificates — construction contract</span>
            <span className="dim" style={{ fontSize: 12 }}>{(p.certificates ?? []).length} issued</span>
          </div>
          {[...(p.certificates ?? [])].sort((a: any, b: any) => String(b.date ?? "").localeCompare(String(a.date ?? ""))).map((ct: any, i: number) => (
            <div key={ct.id} style={{ padding: "11px 16px", borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div className="row between wrap" style={{ gap: 8 }}>
                <span className="row wrap" style={{ gap: 10, alignItems: "baseline", minWidth: 0 }}>
                  <span className="badge accent mono" style={{ fontSize: 10.5 }}>{ct.number}</span>
                  <b style={{ fontSize: 13.5 }}>{ct.title}</b>
                  <span className="dim" style={{ fontSize: 12 }}>{ct.payee}</span>
                </span>
                <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{money(ct.amount, ct.currency)}</span>
              </div>
              {ct.description && <div className="dim" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>{ct.description}</div>}
              <div className="row wrap" style={{ gap: 8, marginTop: 8, alignItems: "center" }}>
                {ct.shareUrl && <a className="btn ghost sm" href={ct.shareUrl} target="_blank" rel="noreferrer">Open certificate</a>}
                {ct.invoice?.url && <a className="btn ghost sm" href={ct.invoice.url} target="_blank" rel="noreferrer">Invoice · {ct.invoice.name}</a>}
                <span className="dim" style={{ fontSize: 11.5 }}>{ct.date}{ct.receiptConfirmedBy ? ` · receipt confirmed by ${ct.receiptConfirmedBy}` : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* variations awaiting the client's approval */}
      {pendingVars.map((v: any) => (
        <div key={v.id} className="card reveal" style={{ padding: "14px 16px", marginBottom: 16, borderLeft: "3px solid var(--warn)" }}>
          <div className="row between wrap" style={{ gap: 8 }}>
            <b style={{ fontSize: 14 }}>{v.ref} — {v.title}</b>
            <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{money(v.amount, p.billing?.currency ?? p.currency)}</span>
          </div>
          <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.55 }}>{v.description}</div>
          {v.reason && <div className="dim" style={{ fontSize: 11.5, marginTop: 3 }}>Reason: {v.reason}</div>}
          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button className="btn accent sm" onClick={() => actions.decideVariation(p.id, v.id, true, p.client.name)}><Icon.check width={13} />Approve variation</button>
            <button className="btn ghost sm" onClick={() => { const note = prompt("Reason for declining (optional)") ?? undefined; actions.decideVariation(p.id, v.id, false, p.client.name, note || undefined); }}>Decline</button>
          </div>
        </div>
      ))}
      {/* advance retainer — an up-front slice of the fee, shown once the schedule is issued */}
      {p.billing?.issuedOn && p.billing.retainer && (
        <div className="card reveal" style={{ padding: "12px 16px", marginBottom: 16, borderLeft: "3px solid var(--accent)" }}>
          <div className="row between wrap" style={{ gap: 8 }}>
            <b style={{ fontSize: 13.5 }}>Advance retainer — {p.billing.retainer.pct}% of the design fee</b>
            <span className="mono" style={{ fontSize: 14 }}>{money(p.billing.retainer.amount, p.billing.currency)}</span>
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.5 }}>Payable up-front on appointment{p.billing.retainer.invoiceId ? " — invoiced separately" : ""}; credited back as each design stage is billed.</div>
        </div>
      )}
      {/* where the money stands, in every mode the project can be billed */}
      {p.billing?.issuedOn && (() => {
        const t = billingTotals(p);
        if (!t) return null;
        const cur = p.billing.currency;
        const cell = { flex: "1 1 140px", padding: "12px 18px", borderRight: "1px solid var(--line)" } as const;
        return (
          <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
            <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
              <span className="kicker">Where the money stands</span>
              <span className="dim" style={{ fontSize: 12 }}>{p.billing.paymentTermsDays ? `${p.billing.paymentTermsDays}-day terms` : ""}{p.billing.retentionPct ? ` · ${p.billing.retentionPct}% retention` : ""}</span>
            </div>
            <div className="row wrap" style={{ gap: 0 }}>
              <div style={cell}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Contract value</div>
                <div className="serif" style={{ fontSize: 21, marginTop: 3 }}>{money(t.billable, cur)}</div>
              </div>
              <div style={cell}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Invoiced to date</div>
                <div className="serif" style={{ fontSize: 21, marginTop: 3 }}>{money(t.invoiced, cur)}</div>
              </div>
              <div style={cell}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Paid</div>
                <div className="serif" style={{ fontSize: 21, marginTop: 3, color: "var(--ok)" }}>{money(t.paid, cur)}</div>
              </div>
              <div style={{ ...cell, background: "color-mix(in srgb,var(--accent) 6%,transparent)" }}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Balance due</div>
                <div className="serif" style={{ fontSize: 21, marginTop: 3, color: "var(--accent-ink)" }}>{money(t.dueNow, cur)}</div>
                <div className="dim" style={{ fontSize: 10.5 }}>invoiced, not yet paid</div>
              </div>
              <div style={{ ...cell, borderRight: "none" }}>
                <div className="kicker" style={{ fontSize: 8.5 }}>Still to bill</div>
                <div className="serif" style={{ fontSize: 21, marginTop: 3 }}>{money(t.remainingToBill, cur)}</div>
                <div className="dim" style={{ fontSize: 10.5 }}>future work</div>
              </div>
            </div>
            {p.billing.deferred && t.deferred > 0 && (
              <div style={{ padding: "10px 18px", borderTop: "1px solid var(--line)", background: "color-mix(in srgb, var(--warn) 7%, transparent)" }}>
                <b style={{ fontSize: 12.5 }}>{money(t.deferred, cur)} earned and deferred</b>
                <div className="dim" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.5 }}>Held back at {Math.round(p.billing.deferred.pct * 100)}% — payable {p.billing.deferred.trigger}</div>
              </div>
            )}
            {t.taxInvoiced > 0 && (
              <div className="row between wrap" style={{ padding: "9px 18px", borderTop: "1px solid var(--line)", gap: 10, fontSize: 12 }}>
                <span className="dim">{p.billing.tax?.label || "VAT"} (on top of the fee)</span>
                <span className="row" style={{ gap: 14 }}>
                  <span><span className="dim">invoiced</span> <span className="mono">{money(t.taxInvoiced, cur)}</span></span>
                  <span><span className="dim">paid</span> <span className="mono" style={{ color: "var(--ok)" }}>{money(t.taxPaid, cur)}</span></span>
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* certified by valuation — the stage sheets you signed off */}
      {p.billing?.issuedOn && p.billing.billingMode === "valuation" && (p.billing.valuations ?? []).length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
          <div className="row between" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)" }}>
            <span className="kicker">Valuations</span>
            <span className="dim" style={{ fontSize: 12 }}>Billed on work certified complete</span>
          </div>
          {p.billing.valuations.map((v: any, i: number) => {
            const t = valuationTotals(v);
            const draw = v.monthly ? monthlyDraw(v) : null;
            const inv = p.invoices.find((x: any) => x.id === v.invoiceId);
            return (
              <div key={v.id} style={{ padding: "13px 18px", borderTop: i ? "1px solid var(--line)" : "none" }}>
                <div className="row between wrap" style={{ gap: 10 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{v.stage}</span>
                    <span className="dim" style={{ fontSize: 11.5, display: "block", marginTop: 2 }}>
                      {draw
                        ? `Monthly draw · ${draw.monthsDrawn} of ${draw.months} months drawn · ${money(draw.balance, p.billing.currency)} remaining`
                        : `${Math.round((t.value ? t.subTotal / t.value : 0) * 100)}% complete${v.certifiedOn ? ` · certified ${fmtDate(v.certifiedOn)}` : ""}`}
                    </span>
                  </span>
                  <span className="row" style={{ gap: 10, flexShrink: 0 }}>
                    <span className="mono" style={{ fontSize: 13 }}>{money(draw ? draw.value : t.value, p.billing.currency)}</span>
                    {inv ? <span className={`badge ${inv.status === "paid" ? "ok" : "accent"}`} style={{ fontSize: 9 }}>{inv.status === "paid" ? "Paid" : "Invoiced"}</span>
                      : <span className="badge" style={{ fontSize: 9 }}>In progress</span>}
                  </span>
                </div>
                {v.lines.length > 1 && (
                  <div style={{ marginTop: 8 }}>
                    {v.lines.map((l: any) => (
                      <div key={l.id} className="row between" style={{ padding: "3px 0", gap: 10 }}>
                        <span className="dim" style={{ fontSize: 12 }}>{l.scope} · {Math.round(l.pctComplete * 100)}%</span>
                        <span className="mono dim" style={{ fontSize: 11.5 }}>{money(Math.round(l.value * l.pctComplete), p.billing.currency)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* the agreed milestone structure, where the project is billed that way */}
      {p.billing?.issuedOn && p.billing.billingMode !== "valuation" && (p.billing.milestones ?? []).length > 0 && (
        <div className="card reveal" style={{ overflow: "hidden", marginBottom: 20 }}>
          <div className="row between wrap" style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", gap: 10 }}>
            <span className="kicker">Payment schedule</span>
          </div>
          {p.billing.milestones.map((m: any, i: number) => {
            const inv = p.invoices.find((x: any) => x.id === m.invoiceId);
            const isPaid = inv?.status === "paid";
            // a payment link points straight at one milestone — highlight it
            const focused = typeof location !== "undefined" && new URLSearchParams(location.search).get("m") === m.id;
            return (
              <div key={m.id} style={{ borderTop: i ? "1px solid var(--line)" : "none", background: focused ? "color-mix(in srgb,var(--accent) 6%,transparent)" : "transparent" }}>
              <div className="row between wrap" style={{ padding: "11px 18px", gap: 10 }}>
                <span className="row" style={{ gap: 10, minWidth: 0 }}>
                  <span style={{ width: 18, height: 18, borderRadius: 18, flexShrink: 0, display: "grid", placeItems: "center", background: isPaid ? "var(--ok)" : "transparent", border: "1.5px solid " + (isPaid ? "var(--ok)" : m.status === "invoiced" ? "var(--accent)" : "var(--line-2)"), color: "#fff" }}>
                    {isPaid && <Icon.check width={10} />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 500 }}>{m.label}</span>
                    {m.basis && <span className="dim" style={{ fontSize: 11.5, display: "block" }}>{m.basis}</span>}
                  </span>
                </span>
                <span className="row" style={{ gap: 10, flexShrink: 0 }}>
                  <span className="mono" style={{ fontSize: 13 }}>{money(m.amount, p.billing.currency)}</span>
                  {isPaid ? <span className="badge ok" style={{ fontSize: 9 }}>Paid</span>
                    : m.status === "invoiced" ? <span className="badge accent" style={{ fontSize: 9 }}>Invoiced</span>
                    : <span className="badge" style={{ fontSize: 9 }}>{m.dueOn ? fmtDate(m.dueOn) : "Upcoming"}</span>}
                </span>
              </div>
              {(m.items ?? []).length > 0 && (
                <div style={{ padding: "0 18px 12px 46px" }}>
                  <div className="kicker" style={{ fontSize: 8.5, marginBottom: 5 }}>What this covers</div>
                  {m.items.map((it: any) => (
                    <div key={it.id} className="row between" style={{ padding: "3px 0", gap: 10 }}>
                      <span className="dim" style={{ fontSize: 12 }}>{it.label}{it.note ? " · " + it.note : ""}</span>
                      {it.amount ? <span className="mono dim" style={{ fontSize: 11.5 }}>{money(it.amount, p.billing.currency)}</span> : null}
                    </div>
                  ))}
                </div>
              )}
              </div>
            );
          })}
        </div>
      )}

      <div className="card reveal" style={{ overflow: "hidden" }}>
        <div className="row between" style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }}>
          <span className="kicker">Invoices</span>
        </div>
        {p.invoices.length === 0 && <div className="dim" style={{ padding: 20, fontSize: 13 }}>No invoices issued yet.</div>}
        {p.invoices.length === 0 && <div className="dim" style={{ padding: 20, fontSize: 13 }}>No invoices issued yet.</div>}
        {p.invoices.map((inv: any, i: number) => {
          const due = daysUntil(inv.dueOn);
          const received = confirmedPaid(inv);
          const claimed = claimedPaid(inv);
          const owedOn = Math.max(0, inv.amount - received);
          /* Telling us you've paid is not the same as having paid, so this
             submits evidence for the studio to check rather than settling
             anything. It stays open until the money is seen in the account. */
          const canSubmit = (inv.status === "due" || inv.status === "overdue") && owedOn - claimed > 0.5;
          return (
            <div key={inv.id} style={{ borderTop: i ? "1px solid var(--line)" : "none" }}>
              <div className="row between" style={{ padding: "16px 20px", gap: 12 }}>
                <div><div className="row" style={{ gap: 8 }}><span className="mono dim" style={{ fontSize: 11.5 }}>{inv.ref}</span><StatusBadge status={inv.status} /></div><div style={{ fontWeight: 500, marginTop: 4 }}>{inv.title}</div>
                  {(inv.items ?? []).length > 0 && <div className="dim" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>{inv.items.map((it: any) => <div key={it.label}>{it.label} · {money(it.amount, p.currency)}</div>)}</div>}<div className="dim" style={{ fontSize: 12, marginTop: 2 }}>{inv.status === "paid" ? `Paid ${fmtDate(inv.paidOn)}` : inv.status === "scheduled" ? `Scheduled for ${fmtDate(inv.dueOn)}` : `Issued ${fmtDate(inv.issuedOn)} · due ${fmtDate(inv.dueOn)}${due < 0 ? ` · ${-due}d overdue` : ""}`}</div></div>
                <div style={{ textAlign: "right" }}>
                  <div className="serif" style={{ fontSize: 20 }}>{money(inv.amount, p.currency)}</div>
                  {inv.taxRate > 0 && inv.net != null && <div className="dim" style={{ fontSize: 11, marginTop: 1 }}>{money(inv.net, p.currency)} + {(inv.taxRate * 100).toFixed(inv.taxRate * 100 % 1 ? 1 : 0)}% {inv.taxLabel || "VAT"}</div>}
                  {owedOn > 0 && received > 0 && <div className="dim" style={{ fontSize: 11.5, marginTop: 2 }}>{money(owedOn, p.currency)} still due</div>}
                  {canSubmit && (inv.payUrl || p.billing?.payUrl) && <a className="btn accent sm" style={{ marginTop: 6, marginRight: 6 }} href={inv.payUrl || p.billing?.payUrl} target="_blank" rel="noreferrer"><Icon.link width={13} />Pay online</a>}
                  {canSubmit && <button className="btn ghost sm" style={{ marginTop: 6, marginRight: 6 }} onClick={() => setProofFor(inv.id)}><Icon.download width={13} style={{ transform: "rotate(180deg)" }} />Send proof</button>}
                  {inv.status !== "scheduled" && (
                    <button className="btn soft sm" style={{ marginTop: 6 }} onClick={() => void downloadInvoicePdf(p, inv)} title={inv.status === "paid" ? "Download the receipted invoice" : "Download this invoice as a PDF"}>
                      <Icon.download width={13} />{inv.status === "paid" ? "Receipt PDF" : "Invoice PDF"}
                    </button>
                  )}
                </div>
              </div>
              {/* every payment against this invoice — confirmed, or waiting on us */}
              {(inv.payments ?? []).length > 0 && (
                <div style={{ padding: "0 20px 14px" }}>
                  <div className="panel" style={{ padding: "9px 13px" }}>
                    <div className="kicker" style={{ fontSize: 8.5, marginBottom: 5 }}>Payments</div>
                    {inv.payments.map((pay: any) => {
                      const pending = pay.status === "claimed";
                      return (
                        <div key={pay.id} className="row between wrap" style={{ padding: "3px 0", gap: 10 }}>
                          <span className="dim" style={{ fontSize: 12 }}>
                            {fmtDate(pay.on)}{pay.method ? ` · ${pay.method}` : ""}{pay.reference ? ` · ref ${pay.reference}` : ""}
                            {pending && <> <span className="badge warn" style={{ fontSize: 8.5 }}>awaiting confirmation</span></>}
                          </span>
                          <span className="mono" style={{ fontSize: 12, color: pending ? "var(--ink-3)" : "var(--ok)" }}>{money(pay.amount, p.currency)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 20 }}><UpcomingCard p={p} /></div>

      <p className="dim reveal" style={{ fontSize: 12.5, marginTop: 14, textAlign: "center" }}>
        Send your transfer slip against an invoice and the studio confirms it once the money lands. A receipt is issued for every confirmed payment.
      </p>

      {proofFor && <ProofModal p={p} invoiceId={proofFor} onClose={() => setProofFor(null)} />}
    </div>
  );
}

/* The client's side of a payment: what they sent, when, and the slip to prove
   it. Nothing here settles the invoice — the studio confirms against the
   account, which is the only place money becomes real. */
function ProofModal({ p, invoiceId, onClose }: { p: any; invoiceId: string; onClose: () => void }) {
  const inv = p.invoices.find((i: any) => i.id === invoiceId);
  const outstandingOn = Math.max(0, inv.amount - confirmedPaid(inv) - claimedPaid(inv));
  const [amount, setAmount] = useState(String(outstandingOn));
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [proof, setProof] = useState<{ url: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const n = Number(amount) || 0;

  async function pickFile(f?: File) {
    if (!f) return;
    setBusy(true); setErr(null);
    try { setProof(await storeFile(f)); }
    catch { setErr("That didn't upload. Try a photo or PDF under 25MB."); }
    finally { setBusy(false); }
  }

  return (
    <Modal open onClose={onClose} title={`Proof of payment — ${inv.ref}`}>
      <div className="col" style={{ gap: 12 }}>
        <div className="panel" style={{ padding: "10px 13px", fontSize: 12.5 }}>
          <div className="row between"><span className="dim">Invoiced</span><span className="mono">{money(inv.amount, p.currency)}</span></div>
          <div className="row between" style={{ marginTop: 4 }}><span className="dim">Outstanding</span><span className="mono">{money(outstandingOn, p.currency)}</span></div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>Amount sent</label>
            <input className="input mono" autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}><label>Date sent</label>
            <input className="input" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
          </div>
        </div>
        <div className="row" style={{ gap: 10 }}>
          <div className="field" style={{ flex: 1 }}><label>How</label>
            <select className="select" value={method} onChange={(e) => setMethod(e.target.value)}>
              {["Bank transfer", "Cheque", "Card", "Cash", "Other"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          <div className="field" style={{ flex: 1 }}><label>Reference</label>
            <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transfer ref" />
          </div>
        </div>
        <div className="field"><label>Anything we should know (optional)</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Sent from the company account" />
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" hidden onChange={(e) => pickFile(e.target.files?.[0])} />
          {proof
            ? <div className="row between panel" style={{ padding: "9px 12px" }}>
                <span className="row" style={{ gap: 8, fontSize: 12.5, minWidth: 0 }}><Icon.check width={14} style={{ color: "var(--ok)", flexShrink: 0 }} />{proof.name}</span>
                <button className="btn ghost sm" onClick={() => setProof(null)}>Replace</button>
              </div>
            : <button className="btn soft block" disabled={busy} onClick={() => fileRef.current?.click()}>
                <Icon.download width={15} style={{ transform: "rotate(180deg)" }} />{busy ? "Uploading…" : "Attach the transfer slip or receipt"}
              </button>}
        </div>
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        <div className="dim" style={{ fontSize: 12, lineHeight: 1.55 }}>
          This tells the studio you've sent it — the invoice stays open until they confirm the money has landed.
        </div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn accent" disabled={n <= 0 || busy}
            onClick={() => {
              actions.claimInvoicePayment(p.id, invoiceId, {
                amount: n, on, method, reference, note,
                proof: proof ?? undefined, by: p.client.name,
              });
              onClose();
            }}>
            <Icon.send width={15} />Send {money(n, p.currency)}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- Messages */
function MessagesTab({ p }: { p: any }) {
  const [body, setBody] = useState("");
  const [context, setContext] = useState("General");
  const [reply, setReply] = useState<Record<string, string>>({});
  function send() { if (!body.trim()) return; actions.addFeedback(p.id, "client", p.client.name, context, body); setBody(""); }
  function sendReply(fid: string) {
    const b = (reply[fid] ?? "").trim(); if (!b) return;
    actions.replyFeedback(p.id, fid, "client", p.client.name, b);
    setReply((x) => ({ ...x, [fid]: "" }));
  }
  return (
    <div style={{ maxWidth: 720 }}>
      <h2 className="serif reveal" style={{ fontSize: 30, marginBottom: 18 }}>Conversation</h2>
      <div className="card pad reveal" style={{ marginBottom: 20 }}>
        <div className="field" style={{ marginBottom: 12 }}><label>About</label>
          <select className="select" value={context} onChange={(e) => setContext(e.target.value)}>
            <option>General</option>
            {p.presentation.spaces.map((s: any) => <option key={s.id}>{s.name}</option>)}
          </select>
        </div>
        <textarea className="textarea" placeholder="Share a thought with the studio…" value={body} onChange={(e) => setBody(e.target.value)} />
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}><button className="btn accent" onClick={send}><Icon.send width={15} />Send</button></div>
      </div>

      <div className="col" style={{ gap: 14 }}>
        {p.feedback.map((f: any) => (
          <div key={f.id} className="card pad reveal">
            <div className="row between"><div className="row" style={{ gap: 10 }}><Avatar name={f.authorName} hue={f.author === "client" ? p.client.avatarHue : 20} size={32} /><div><div style={{ fontWeight: 600, fontSize: 14 }}>{f.authorName}</div><div className="dim" style={{ fontSize: 11.5 }}>{f.context} · {relative(f.createdOn)}</div></div></div>{f.resolved && <span className="badge ok" style={{ fontSize: 10 }}>Resolved</span>}</div>
            <p style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.55 }}>{f.body}</p>
            {f.replies.map((r: any) => (
              <div key={r.id} style={{ marginTop: 12, paddingLeft: 12, borderLeft: "2px solid " + (r.author === "client" ? "var(--accent-soft)" : "var(--sky)") }}>
                <div className="row" style={{ gap: 8 }}><span style={{ fontWeight: 600, fontSize: 13 }}>{r.authorName}</span><span className="badge" style={{ fontSize: 8.5 }}>{r.author}</span><span className="dim" style={{ fontSize: 11 }}>{relative(r.createdOn)}</span></div>
                <p className="muted" style={{ fontSize: 14, marginTop: 3 }}>{r.body}</p>
              </div>
            ))}
            {/* the client can carry the thread on, not just start it */}
            <div className="row" style={{ gap: 8, marginTop: 12 }}>
              <input className="input" style={{ fontSize: 13.5 }} placeholder="Reply…" value={reply[f.id] ?? ""} onChange={(e) => setReply((x) => ({ ...x, [f.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && sendReply(f.id)} />
              <button className="btn soft sm" onClick={() => sendReply(f.id)}><Icon.send width={14} /></button>
            </div>
          </div>
        ))}
        {p.feedback.length === 0 && <div className="dim">No messages yet — start a conversation above.</div>}
      </div>
    </div>
  );
}
